import { resolvePhysicalTrayQuantity } from "./physicalTrayQuantity";
import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import { customerDeliveryAddressesApi } from "@/features/customers/services/customerDeliveryAddressesApi";
import { useCreateOrder, useCalculateProductionStage } from "../../hooks/useOrders";
import { useActiveTransportRates } from "@/features/dispatch/hooks/useTransportRates";
import { useCreateSeedBatch } from "../../hooks/useSeedBatches";
import { useVarietyPricingsLookup } from "../../hooks/useVarietyPricings";
import type { VarietyPricing } from "../../services/varietyPricingApi";
import { availableInOrderUnits, stockMatchesOrder, isPerSeedlingPricing, isPerTrayPricing, commercialOrderQuantity } from "../../utils/orderStockAvailability";
import { useActiveCropVarieties } from "../../hooks/useCropVarieties";
import { useActiveCropTypes } from "../../hooks/useCropTypes";
import { useAvailableGreenhouseStock } from "../../hooks/useGreenhouseStock";
import { useEffectiveDiscount } from "../../hooks/useWalkInOrders";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useEarningPreview } from "@/features/loyalty/hooks/usePoints";
import {
  CompanySeedlingCategorySelector,
  type SeedlingCategory,
} from "../shared/CompanySeedlingCategorySelector";
import { type CreateSeedBatchRequest } from "../../services/seedBatchApi";
import type { SeedBatch } from "../../services/seedBatchApi";
import type { ProductionStageRecommendation } from "../../services/ordersApi";
import { cropTypesApi } from "../../services/cropTypesApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Calculator, ChevronsUpDown, Check } from "lucide-react";
import { CustomerSearchSelect } from "@/shared/components/CustomerSearchSelect";
import { PhoneNumberInput } from "@/shared/components/PhoneNumberInput";
import { MapLocationPicker } from "@/shared/components/MapLocationPicker";
import { useUnitsOfMeasure } from "@/shared/hooks/useUnitsOfMeasure";
import { useNotification } from "@/shared/hooks/useNotification";
import { useTrayDepositRates } from "@/shared/hooks/useTrayDepositRates";
import { useApiError } from "@/shared/hooks/useApiError";
import { getErrorMessage } from "@/shared/utils/errorHandler";
import { FieldError as InlineFieldError } from "@/shared/components/forms/FieldError";
import { useFieldErrorActions, useFormFieldErrors } from "@/shared/contexts/field-error-context";
import type { ApiFieldError } from "@/shared/types/apiError";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

// Helper to safely format dates - returns "-" for null/undefined/invalid dates
const formatDateSafe = (date: string | Date | null | undefined): string => {
  if (!date) return "-";
  const d = new Date(date);
  // Check for invalid date or epoch (1/1/1970)
  if (isNaN(d.getTime()) || d.getFullYear() < 1971) return "-";
  return d.toLocaleDateString();
};

const is442HoleTray = (trayType: unknown): boolean =>
  Number(trayType) === 442 || trayType === "FourFortyTwoHole";

const trayTypeNumber = (trayType: unknown): 210 | 442 =>
  is442HoleTray(trayType) ? 442 : 210;

interface OrderItemInput {
  tempId: string;
  cropVarietyId?: string;
  varietyPricingId?: string;
  isCompanySeedling: boolean;
  quantity: string;
  trayType: string;
  notes: string;
  variety?: string;
  quantityReceived?: string;  // legacy display only
  numberOfPacks?: string;
  quantityPerPack?: string;
  seedFormat: "packets" | "pieces"; // packets = packs × quantityPerPack; pieces = loose count
  unit: string;
  // Seed batch fields for customer seeds (not seedBatchId - we create it)
  qualityNotes?: string;
  storageLocation?: string;
  cropTypeId?: string;
  lotNumber?: string; // lot/batch number printed on the customer's seed packet (required)
  packagingDate?: string; // packaging date printed on the customer's seed packet (required, ISO yyyy-MM-dd)
  selectedStockEntryId?: string; // greenhouse stock entry to reserve on order creation
  selectedStockAvailableQuantity?: number; // snapshot availability in commercial order units
  pricingSnapshot?: VarietyPricing;
}

interface FormData {
  customerId: string;
  expectedDeliveryDate: string;
  fulfilmentMethod: "CustomerCollection" | "Delivery";
  transportRateId: string;
  deliveryAreaId: string;
  manualTransportCost: string;
  deliveryLocation: string;
  deliveryLatitude: number | null;
  deliveryLongitude: number | null;
  deliveryInstructions: string;
  deliveryContactPerson: string;
  deliveryContactPhone: string;
  includesTrayDeposit: boolean; // refundable tray deposit on PerTray items
  plannedPlantingDate: string;
  specialInstructions: string;
  notes: string;
}

const initialFormData: FormData = {
  customerId: "",
  expectedDeliveryDate: "",
  fulfilmentMethod: "CustomerCollection",
  transportRateId: "",
  deliveryAreaId: "",
  manualTransportCost: "",
  deliveryLocation: "",
  deliveryLatitude: null,
  deliveryLongitude: null,
  deliveryInstructions: "",
  deliveryContactPerson: "",
  deliveryContactPhone: "",
  includesTrayDeposit: false,
  plannedPlantingDate: "",
  specialInstructions: "",
  notes: "",
};

const initialItemData: OrderItemInput = {
  tempId: "",
  cropVarietyId: "",
  varietyPricingId: "",
  isCompanySeedling: true,
  quantity: "",
  trayType: "210",
  notes: "",
  variety: "",
  quantityReceived: "",
  seedFormat: "packets",
  unit: "1",
  qualityNotes: "",
  storageLocation: "",
  cropTypeId: "",
  lotNumber: "",
  packagingDate: "",
  selectedStockEntryId: "",
  selectedStockAvailableQuantity: 0,
};

const CREATE_ORDER_FORM_ID = "createOrder";
const FORM_LEVEL_FIELD = "_form";

const makeFieldError = (field: string, userMessage: string, code = "INVALID_VALUE"): ApiFieldError => ({
  field,
  code,
  userMessage,
});

const itemFieldPath = (index: number, field: string) => `orderItems.${index}.${field}`;

const mapOrderValidationField = (field: string): string => {
  const normalized = field
    .replace(/^OrderItems/i, "orderItems")
    .replace(/^Items/i, "items")
    .replace(/\[(\d+)\]\./g, ".$1.")
    .replace(/^\./, "");

  const itemMatch = normalized.match(/^(?:orderItems|items)\.(\d+)\.(.+)$/i);
  if (itemMatch) {
    const [, index, rawChild] = itemMatch;
    const child = rawChild.charAt(0).toLowerCase() + rawChild.slice(1);
    const mappedChild =
      child === "pricingOptionId" || child === "varietyPricingId"
        ? "pricingOptionId"
        : child === "cropVarietyId" || child === "varietyId"
          ? "varietyId"
          : child;
    return `orderItems.${index}.${mappedChild}`;
  }

  const lowerFirst = normalized.charAt(0).toLowerCase() + normalized.slice(1);
  const topLevelMap: Record<string, string> = {
    customerId: "customerId",
    cropTypeId: "cropTypeId",
    varietyId: "varietyId",
    cropVarietyId: "varietyId",
    quantity: "quantity",
    pricingOptionId: "pricingOptionId",
    varietyPricingId: "pricingOptionId",
    fulfilmentMethod: "fulfilmentMethod",
    transportRateId: "transportRateId",
    manualTransportCost: "manualTransportCost",
    deliveryLocation: "deliveryLocation",
    deliveryInstructions: "deliveryInstructions",
    deliveryContactPerson: "deliveryContactPerson",
    deliveryContactPhone: "deliveryContactPhone",
    contactPerson: "deliveryContactPerson",
    contactPhone: "deliveryContactPhone",
    plannedPlantingDate: "plannedPlantingDate",
    expectedDeliveryDate: "expectedDeliveryDate",
    orderItems: "orderItems",
    items: "orderItems",
  };

  return topLevelMap[lowerFirst] ?? FORM_LEVEL_FIELD;
};

const mapOrderFieldErrors = (errors: ApiFieldError[]): ApiFieldError[] =>
  errors.map((error) => ({
    ...error,
    field: mapOrderValidationField(error.field),
  }));

// ── Dialog ────────────────────────────────────────────────────────────────

export const CreateOrderDialog = () => {
  const [open, setOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [requestSpecialDiscount, setRequestSpecialDiscount] = useState(false);
  const [specialDiscountKind, setSpecialDiscountKind] = useState<"Percentage" | "FixedAmount">("Percentage");
  const [specialDiscountValue, setSpecialDiscountValue] = useState("");
  const [specialDiscountReason, setSpecialDiscountReason] = useState("");
  const canRequestDiscount = useAuthStore(state => state.hasPermission("discounts.request"));
  const canManageAddresses = useAuthStore(state => state.hasPermission("customers.update"));
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [selectedSavedAddressId, setSelectedSavedAddressId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [itemData, setItemData] = useState(initialItemData);
  const [orderItems, setOrderItems] = useState<OrderItemInput[]>([]);
  const [itemCounter, setItemCounter] = useState(0);
  const [stageResults, setStageResults] = useState<Record<string, ProductionStageRecommendation>>({});
  const [calculatingItems, setCalculatingItems] = useState<Set<string>>(new Set());
  const [cropTypeComboOpenCS, setCropTypeComboOpenCS] = useState(false);
  const transportRatesQuery = useActiveTransportRates();
  const activeTransportRates = transportRatesQuery.data ?? [];
  const selectedTransportRate = activeTransportRates.find((rate) => rate.id === formData.transportRateId);
  const notify = useNotification();
  const [addressLabel, setAddressLabel] = useState("");
  const savedAddressesQuery = useQuery({
    queryKey: ["customer-delivery-addresses", formData.customerId],
    queryFn: () => customerDeliveryAddressesApi.list(formData.customerId),
    enabled: !!formData.customerId && formData.fulfilmentMethod === "Delivery",
  });
  const saveAddressMutation = useMutation({
    mutationFn: () => customerDeliveryAddressesApi.create(formData.customerId, {
      label: addressLabel.trim(),
      deliveryAreaId: formData.deliveryAreaId || selectedTransportRate?.deliveryAreas?.[0]?.id || "",
      address: formData.deliveryLocation.trim(),
      latitude: formData.deliveryLatitude,
      longitude: formData.deliveryLongitude,
    }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["customer-delivery-addresses", formData.customerId] });
      setAddressLabel("");
      notify.success("Delivery address saved.");
    },
    onError: (error: Error) => notify.error(error.message || "Could not save the address."),
  });
  const [varietyComboOpen, setVarietyComboOpen] = useState(false);
  const [varietyComboOpenCS, setVarietyComboOpenCS] = useState(false);
  const [companySeedlingCategory, setCompanySeedlingCategory] = useState<SeedlingCategory>("vegetable");
  const trayRates = useTrayDepositRates();

  // Effective discount preview for the selected customer (informational — the backend re-resolves
  // and applies the same discount at creation). Disabled until a customer is chosen.
  const discountQuery = useEffectiveDiscount(formData.customerId || null);

  // Server-paged by cropTypeId so we don't load every active pricing in the system.
  // The dialog still does client-side filtering by variety/side because the variety
  // picker needs per-variety pricing previews for the selected crop type.
  const varietyPricingsPage = useVarietyPricingsLookup({
    isActive: true,
    cropTypeId: itemData.cropTypeId || undefined,
    cropVarietyId: itemData.cropVarietyId || undefined,
    pageSize: 100,
  }, open && !!itemData.cropTypeId);
  const pricingOptions = useMemo(() => varietyPricingsPage.data?.pages.flatMap((page) => page.items) ?? [], [varietyPricingsPage.data]);
  const lookupPricing = (id: string | undefined | null) => {
    if (!id) return undefined;
    return (
      pricingOptions.find((pricing) => pricing.id === id) ??
      orderItems.find((item) => item.varietyPricingId === id)?.pricingSnapshot
    );
  };
  const varietiesQuery = {
    data: pricingOptions,
    isLoading: varietyPricingsPage.isLoading,
    isFetching: varietyPricingsPage.isFetching,
    hasNextPage: varietyPricingsPage.hasNextPage,
    fetchNextPage: varietyPricingsPage.fetchNextPage,
    isFetchingNextPage: varietyPricingsPage.isFetchingNextPage,
  };
  const cropVarietiesQuery = useActiveCropVarieties();
  const cropTypesQuery = useActiveCropTypes();
  const createOrderMutation = useCreateOrder();
  const createSeedBatchMutation = useCreateSeedBatch();
  const calcMutation = useCalculateProductionStage();
  const unitsQuery = useUnitsOfMeasure();
  const handleApiError = useApiError();
  const fieldErrors = useFormFieldErrors(CREATE_ORDER_FORM_ID);
  const { setFieldErrors, clearField, clearForm } = useFieldErrorActions();
  const fieldErrorMap = useMemo(() => {
    const map: Record<string, ApiFieldError[]> = {};
    for (const error of fieldErrors) {
      map[error.field] = [...(map[error.field] ?? []), error];
    }
    return map;
  }, [fieldErrors]);

  const hasFieldError = useCallback(
    (field: string) => Boolean(fieldErrorMap[field]?.length),
    [fieldErrorMap]
  );

  const clearOrderField = useCallback(
    (field: string) => {
      clearField(CREATE_ORDER_FORM_ID, field);
      clearField(CREATE_ORDER_FORM_ID, FORM_LEVEL_FIELD);
    },
    [clearField]
  );

  const scrollToOrderField = useCallback((field: string) => {
    if (field === FORM_LEVEL_FIELD) return;
    window.setTimeout(() => {
      const selectorField = field.replace(/"/g, '\\"');
      const target = document.querySelector<HTMLElement>(`[data-order-field="${selectorField}"]`);
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      const focusable = target.querySelector<HTMLElement>(
        "input, textarea, button, [role='combobox'], [tabindex]:not([tabindex='-1'])"
      );
      focusable?.focus();
    }, 0);
  }, []);

  const orderFieldError = useCallback(
    (field: string) => (
      <InlineFieldError
        formId={CREATE_ORDER_FORM_ID}
        field={field}
        className="text-red-600 text-xs mt-1"
      />
    ),
    []
  );

  const itemErrorFields = useCallback(
    (index: number) => fieldErrors.filter((error) => error.field.startsWith(`orderItems.${index}.`)),
    [fieldErrors]
  );

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      clearForm(CREATE_ORDER_FORM_ID);
      setShowConfirm(false);
      setRequestSpecialDiscount(false);
      setSpecialDiscountKind("Percentage");
      setSpecialDiscountValue("");
      setSpecialDiscountReason("");
      setFormData(initialFormData);
      setSelectedSavedAddressId("");
      setCustomerName("");
      setItemData(initialItemData);
      setOrderItems([]);
      setItemCounter(0);
      setStageResults({});
      setCalculatingItems(new Set());
      setCropTypeComboOpenCS(false);
      setVarietyComboOpen(false);
      setVarietyComboOpenCS(false);
      setCompanySeedlingCategory("vegetable");
    }
    setOpen(nextOpen);
  };

  const selectedCompanySeedlingCropTypeLabel = useMemo(
    () => (cropTypesQuery.data ?? []).find((ct) => ct.id === itemData.cropTypeId)?.name,
    [cropTypesQuery.data, itemData.cropTypeId]
  );

  const fetchCompanySeedlingCropTypes = useCallback(
    async ({
      search,
      pageNumber,
      pageSize,
      isAgroforestry,
    }: {
      search: string;
      pageNumber: number;
      pageSize: number;
      isAgroforestry: boolean;
    }) => {
      const result = await cropTypesApi.getCropTypesPaged({
        pageNumber,
        pageSize,
        search: search.trim() || undefined,
        isActive: true,
        isAgroforestry,
        sortBy: "name",
        sortDirection: "asc",
      });

      return {
        items: result.items.map((ct) => ({
          value: ct.id,
          label: ct.name,
          isAgroforestry: ct.isAgroforestry,
        })),
      };
    },
    []
  );

  // Varieties filtered by the selected crop type
  const varietiesForCropType = cropVarietiesQuery.data?.filter(
    (v) => v.cropTypeId === itemData.cropTypeId
  ) || [];

  // Does the selected crop type have any varieties in the catalogue?
  const selectedCropTypeHasVarieties = varietiesForCropType.length > 0;

  // Available pricings: by variety if selected, else by crop type with no variety.
  // Filter by side availability — only show pricings where the relevant price is set
  // (company-seedling for company orders, propagation for customer-seed orders).
  const availablePricingsForSelection = (varietiesQuery.data?.filter((v) => {
    const sideAvailable = itemData.isCompanySeedling
      ? v.companySeedlingPrice != null && v.companySeedlingPrice > 0
      : v.propagationServicePrice != null && v.propagationServicePrice > 0;
    if (!sideAvailable) return false;
    if (itemData.cropVarietyId) return v.cropVarietyId === itemData.cropVarietyId;
    if (itemData.cropTypeId && !selectedCropTypeHasVarieties)
      return v.cropTypeId === itemData.cropTypeId && !v.cropVarietyId;
    return false;
  }) || []);

  // Stock advisory — only relevant for company seedlings
  const selectedVariety = itemData.isCompanySeedling
    ? cropVarietiesQuery.data?.find((v) => v.id === itemData.cropVarietyId)
    : undefined;

  // Pricing mode for the currently selected item
  const selectedPricing = varietiesQuery.data?.find((v) => v.id === itemData.varietyPricingId);
  const isPerSeedling = isPerSeedlingPricing(selectedPricing?.pricingMode);
  const stockUnitLabel = isPerSeedling ? "pieces" : "trays";
  const physicalEquivalent = !isPerSeedling && selectedPricing && itemData.quantity
    ? resolvePhysicalTrayQuantity(itemData.quantity, selectedPricing.trayCapacity)
    : null;

  // One crop/variety-scoped request serves both pricing cards and the picker.
  // Keep all tray types in this scope so piece pricing can use any compatible source.
  const stockCropTypeId = selectedVariety?.cropTypeId || itemData.cropTypeId;
  const stockVariety = selectedVariety?.name;
  const stockScopeEnabled = open && itemData.isCompanySeedling && !!stockCropTypeId && cropVarietiesQuery.isSuccess &&
    (!selectedCropTypeHasVarieties || !!selectedVariety);
  const availableEntriesQuery = useAvailableGreenhouseStock({
    cropTypeId: stockCropTypeId || undefined,
    variety: stockVariety,
    enabled: stockScopeEnabled,
  });
  const pricingStockTarget = (pricing: VarietyPricing) => ({
    cropTypeId: pricing.cropTypeId,
    cropTypeName: pricing.cropTypeName,
    variety: pricing.varietyName,
    pricingMode: pricing.pricingMode,
    trayType: pricing.trayType,
  });
  const compatibleAvailableEntries = (availableEntriesQuery.data ?? []).filter((entry) =>
    selectedPricing && stockMatchesOrder(entry, pricingStockTarget(selectedPricing))
  );

  const getStockStatus = (varietyPricingId: string, requestedQty: number) => {
    const v = lookupPricing(varietyPricingId);
    if (!v) return null;
    if (!stockScopeEnabled || v.cropTypeId !== stockCropTypeId ||
        (v.varietyName ?? "").trim().toLowerCase() !== (stockVariety ?? "").trim().toLowerCase()) {
      return { label: "Select crop/variety to check stock", variant: "secondary" as const };
    }
    if (availableEntriesQuery.isLoading) {
      return { label: "Checking stock...", variant: "secondary" as const };
    }

    if (availableEntriesQuery.isError) {
      return { label: "Stock unavailable", variant: "secondary" as const };
    }

    const matchedEntries = (availableEntriesQuery.data ?? []).filter((entry) =>
      entry.isReadyForDispatch && stockMatchesOrder(entry, pricingStockTarget(v)));
    const available = matchedEntries.reduce((sum, e) => sum + availableInOrderUnits(e, v.pricingMode), 0);
    if (available >= requestedQty) return { label: `In Stock (${available} available)`, variant: "default" as const };
    if (available > 0) return { label: `Partial Stock (${available} of ${requestedQty} available)`, variant: "secondary" as const };
    return { label: "No Stock — Sowing Required", variant: "destructive" as const };
  };

  const handleFormChange = (field: keyof FormData, value: string) => {
    clearOrderField(field);
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddItem = () => {
    if (selectedPricing) {
      const error = isPerSeedling
        ? (!Number.isSafeInteger(Number(itemData.quantity)) || Number(itemData.quantity) <= 0 ? "Enter a positive whole number of seedlings." : null)
        : resolvePhysicalTrayQuantity(itemData.quantity, selectedPricing.trayCapacity).error;
      if (error) { notify.error(error); return; }
    }
    // Validation
    if (itemData.isCompanySeedling) {
      if (!itemData.cropTypeId) {
        notify.error("Please select a Crop Type");
        return;
      }
      if (selectedCropTypeHasVarieties && !itemData.cropVarietyId) {
        notify.error("Please select a Variety");
        return;
      }
      if (!itemData.quantity) {
        notify.error("Please enter a Quantity");
        return;
      }
      if (!itemData.varietyPricingId) {
        notify.error("No pricing found for the selection. Please select a pricing option or configure pricing first.");
        return;
      }
    } else {
      if (!itemData.cropTypeId) {
        notify.error("Please select a Crop Type");
        return;
      }
      if (selectedCropTypeHasVarieties && !itemData.cropVarietyId) {
        notify.error("Please select a Variety");
        return;
      }
      if (itemData.seedFormat === "packets") {
        if (!itemData.numberOfPacks || !itemData.quantityPerPack) {
          notify.error("Please fill in Number of Packs and Quantity in Pack");
          return;
        }
      } else {
        if (!itemData.quantityPerPack) {
          notify.error("Please enter the number of pieces/seeds");
          return;
        }
      }
      if (!itemData.storageLocation || !itemData.quantity) {
        notify.error("Please fill in Storage Location and Estimated Quantity");
        return;
      }
      if (!itemData.varietyPricingId) {
        notify.error("No pricing found for the selection. Please select a pricing option or configure pricing first.");
        return;
      }
      if (!itemData.lotNumber?.trim()) {
        notify.error("Please enter the Lot No printed on the seed packet");
        return;
      }
      if (!itemData.packagingDate) {
        notify.error("Please select the Packaging Date printed on the seed packet");
        return;
      }
    }

    const newItem: OrderItemInput = {
      tempId: `item-${itemCounter}-${Date.now()}`,
      cropVarietyId: itemData.cropVarietyId || undefined,
      varietyPricingId: itemData.varietyPricingId,
      isCompanySeedling: itemData.isCompanySeedling,
      quantity: itemData.quantity,
      trayType: itemData.trayType,
      unit: itemData.unit,
      seedFormat: itemData.seedFormat,
      notes: itemData.notes,
      variety: itemData.variety,
      quantityReceived: itemData.quantityReceived,
      numberOfPacks: itemData.seedFormat === "pieces" ? "1" : itemData.numberOfPacks,
      quantityPerPack: itemData.quantityPerPack,
      qualityNotes: itemData.qualityNotes,
      storageLocation: itemData.storageLocation,
      cropTypeId: itemData.cropTypeId,
      lotNumber: itemData.lotNumber,
      packagingDate: itemData.packagingDate,
      selectedStockEntryId: itemData.selectedStockEntryId,
      selectedStockAvailableQuantity: itemData.selectedStockAvailableQuantity,
      pricingSnapshot: lookupPricing(itemData.varietyPricingId),
    };

    setOrderItems([...orderItems, newItem]);
    clearOrderField("orderItems");
    setItemCounter((prev) => prev + 1);
    setItemData(initialItemData);
  };

  const handleRemoveItem = (tempId: string) => {
    setOrderItems(orderItems.filter((i) => i.tempId !== tempId));
    clearForm(CREATE_ORDER_FORM_ID);
    setStageResults((prev) => { const next = { ...prev }; delete next[tempId]; return next; });
  };

  const computeOrderSummary = () => {
    const items = orderItems.map((item) => {
      const pricing = lookupPricing(item.varietyPricingId);
      const price = item.isCompanySeedling
        ? pricing?.companySeedlingPrice
        : pricing?.propagationServicePrice;
      const qty = parseFloat(item.quantity) || 0;
      const subtotal = price != null ? qty * price : null;
      const unitLabel = isPerSeedlingPricing(pricing?.pricingMode) ? "pcs" : "trays";
      return { item, pricing, price, qty, subtotal, unitLabel };
    });

    const trayCount = items
      .filter((x) => !isPerSeedlingPricing(x.pricing?.pricingMode))
      .reduce((s, x) => s + x.qty, 0);

    let transportCost = 0;
    let transportLabel = "";
    if (formData.fulfilmentMethod === "Delivery" && selectedTransportRate) {
      const manualCost = formData.manualTransportCost.trim();
      transportCost = manualCost === ""
        ? selectedTransportRate.costAmount * trayCount
        : Number(manualCost) || 0;
      transportLabel = manualCost === ""
        ? `${selectedTransportRate.destinationName} — KES ${selectedTransportRate.costAmount.toLocaleString("en-KE", { minimumFractionDigits: 2 })}/tray × ${trayCount} trays`
        : `${selectedTransportRate.destinationName} — manual transport charge`;
    }

    const itemsSubtotal = items.reduce((s, x) => s + (x.subtotal ?? 0), 0);

    // Refundable tray deposit — computed client-side for display only; the backend
    // recomputes from system settings at creation time.
    let trayDeposit = 0;
    if (formData.includesTrayDeposit) {
      trayDeposit = items
        .filter((x) => isPerTrayPricing(x.pricing?.pricingMode) || (!x.pricing && x.item.trayType))
        .reduce(
          (s, x) =>
            s + Math.floor(x.qty) * (is442HoleTray(x.item.trayType) ? trayRates.rate442Hole : trayRates.rate210Hole),
          0
        );
    }

    // Customer discount applies to goods only (transport is never discounted), mirroring the backend.
    const discountPct = discountQuery.data?.effectiveDiscountPercentage ?? 0;
    const discountAmount = Math.round(itemsSubtotal * (discountPct / 100) * 100) / 100;
    const discountSource = discountQuery.data?.discountSource;
    const discountDetails = discountQuery.data?.discountDetails ?? null;
    const discountWarning = discountQuery.data?.warning ?? null;

    // Build a user-friendly discount label based on source type
    let discountLabel = "";
    if (discountSource === "GroupCampaign" || discountSource === "GlobalCampaign") {
      // Show campaign name from details, e.g., "Campaign: Test sale"
      const campaignMatch = discountDetails?.match(/^([^(]+)/);
      const campaignName = campaignMatch ? campaignMatch[1].trim() : "Campaign";
      discountLabel = `Campaign: ${campaignName}`;
    } else if (discountSource === "Individual") {
      discountLabel = "Individual";
    } else if (discountSource === "GroupDefault") {
      discountLabel = "Group Default";
    } else if (discountSource) {
      discountLabel = discountSource;
    }

    const grandTotal = itemsSubtotal - discountAmount + transportCost + trayDeposit;
    return {
      items, trayCount, transportCost, transportLabel, itemsSubtotal, trayDeposit,
      discountPct, discountAmount, discountSource, discountDetails, discountLabel, discountWarning, grandTotal,
    };
  };

  // Informational loyalty-points estimate for the order being built. Condition-aware
  // and display-only — the backend evaluates the active config's conditions and the
  // points are NOT credited here (auto-earning is deferred).
  // computeOrderSummary reads the pricing ref-cache (same pattern as the render
  // summaries below); it's populated in an effect and the component re-renders on
  // query updates, so the estimate stays current.
  // eslint-disable-next-line react-hooks/refs
  const pointsSummary = computeOrderSummary();
  const orderProductCategories = Array.from(
    new Set(
      pointsSummary.items
        .map((x) => x.pricing?.cropTypeName)
        .filter((n): n is string => !!n)
    )
  );
  const earningPreviewQuery = useEarningPreview(formData.customerId || undefined, {
    // Points earn on goods only — exclude the transport charge and the refundable tray deposit.
    orderAmount:
      pointsSummary.grandTotal - pointsSummary.transportCost - pointsSummary.trayDeposit,
    productCategories: orderProductCategories,
  });
  const pointsEstimate = earningPreviewQuery.data;
  const showPointsEstimate =
    !!pointsEstimate?.hasActiveConfig && pointsEstimate.qualifies && pointsEstimate.points > 0;

  const handleOpenConfirm = () => {
    const validationErrors = validateOrderBeforeSubmit();
    if (validationErrors.length > 0) {
      setFieldErrors(CREATE_ORDER_FORM_ID, validationErrors);
      scrollToOrderField(validationErrors[0].field);
      return;
    }
    setShowConfirm(true);
  };

  const validateOrderBeforeSubmit = (): ApiFieldError[] => {
    const errors: ApiFieldError[] = [];

    if (!formData.customerId) {
      errors.push(makeFieldError("customerId", "Select a customer."));
    }

    if (orderItems.length === 0) {
      errors.push(makeFieldError("orderItems", "Add at least one order item."));
    }

    orderItems.forEach((item, index) => {
      const itemLabel = item.variety || cropTypesQuery.data?.find((ct) => ct.id === item.cropTypeId)?.name || `item ${index + 1}`;
      if (!item.cropTypeId) {
        errors.push(makeFieldError(itemFieldPath(index, "cropTypeId"), "Select a crop type."));
      }
      if (!item.varietyPricingId) {
        errors.push(makeFieldError(itemFieldPath(index, "pricingOptionId"), `Select a pricing option for ${itemLabel}.`));
      }
      const pricing = varietiesQuery.data?.find((v) => v.id === item.varietyPricingId);
      const quantityError = pricing && !isPerSeedlingPricing(pricing.pricingMode)
        ? resolvePhysicalTrayQuantity(item.quantity, pricing.trayCapacity).error
        : !Number.isSafeInteger(Number(item.quantity)) || Number(item.quantity) <= 0 ? "Enter a positive whole number of seedlings." : null;
      if (quantityError) errors.push(makeFieldError(itemFieldPath(index, "quantity"), quantityError));
      if (!Number.isFinite(parseFloat(item.quantity)) || parseFloat(item.quantity) <= 0) {
        errors.push(makeFieldError(itemFieldPath(index, "quantity"), `Enter a quantity greater than zero for ${itemLabel}.`));
      }
    });

    const expectedDeliveryDate = formData.expectedDeliveryDate ? new Date(formData.expectedDeliveryDate) : null;
    if (expectedDeliveryDate && expectedDeliveryDate > new Date(new Date().setFullYear(new Date().getFullYear() + 5))) {
      errors.push(makeFieldError("expectedDeliveryDate", "Expected delivery date is unrealistically far in the future."));
    }

    const plannedPlantingDate = formData.plannedPlantingDate ? new Date(formData.plannedPlantingDate) : null;
    if (plannedPlantingDate && plannedPlantingDate > new Date(new Date().setFullYear(new Date().getFullYear() + 5))) {
      errors.push(makeFieldError("plannedPlantingDate", "Planned planting date is unrealistically far in the future."));
    }

    if (formData.fulfilmentMethod === "Delivery" && !formData.transportRateId) {
      errors.push(makeFieldError("transportRateId", "Select a pricing region."));
    }
    if (formData.fulfilmentMethod === "Delivery" && (selectedTransportRate?.deliveryAreas?.length ?? 0) > 1 && !formData.deliveryAreaId)
      errors.push(makeFieldError("deliveryAreaId", "Select a delivery area."));
    if (formData.fulfilmentMethod === "Delivery" && formData.manualTransportCost.trim() !== "") {
      const amount = Number(formData.manualTransportCost);
      if (!Number.isFinite(amount) || amount < 0) {
        errors.push(makeFieldError("manualTransportCost", "Enter a valid transport charge of zero or more."));
      }
    }
    if (requestSpecialDiscount) {
      const summary = computeOrderSummary();
      const value = Number(specialDiscountValue);
      const proposedDiscount = specialDiscountKind === "Percentage"
        ? Math.round(summary.itemsSubtotal * value) / 100 : value;
      if (!Number.isFinite(value) || value <= 0 || (specialDiscountKind === "Percentage" && value > 100)
        || proposedDiscount > summary.itemsSubtotal || proposedDiscount <= summary.discountAmount)
        errors.push(makeFieldError(FORM_LEVEL_FIELD, "Special discount must exceed the current discount and cannot exceed the goods subtotal."));
      if (!specialDiscountReason.trim())
        errors.push(makeFieldError(FORM_LEVEL_FIELD, "Enter a reason for the special discount."));
    }
    return errors;
  };

  const handleCalculateStageForItem = (item: OrderItemInput) => {
    if (!formData.plannedPlantingDate) {
      notify.error("Please set the Planned Planting Date first");
      return;
    }

    const cropVarietyId = item.isCompanySeedling ? item.cropVarietyId : undefined;
    const cropTypeId = !item.isCompanySeedling ? item.cropTypeId : undefined;

    setCalculatingItems((prev) => new Set(prev).add(item.tempId));
    calcMutation.mutate(
      {
        ...(cropVarietyId && { cropVarietyId }),
        ...(cropTypeId && { cropTypeId }),
        plannedPlantingDate: new Date(formData.plannedPlantingDate).toISOString(),
      },
      {
        onSuccess: (result) => {
          setStageResults((prev) => ({ ...prev, [item.tempId]: result }));
          setCalculatingItems((prev) => { const next = new Set(prev); next.delete(item.tempId); return next; });
        },
        onError: (error) => {
          handleApiError(error);
          setCalculatingItems((prev) => { const next = new Set(prev); next.delete(item.tempId); return next; });
        },
      }
    );
  };

  const handleSubmit = async () => {
    clearForm(CREATE_ORDER_FORM_ID);
    const validationErrors = validateOrderBeforeSubmit();
    if (validationErrors.length > 0) {
      setFieldErrors(CREATE_ORDER_FORM_ID, validationErrors);
      scrollToOrderField(validationErrors[0].field);
      return;
    }

    try {
      // Create seed batches for customer seed items first
      const seedBatchIds: Map<string, string> = new Map();

      for (const item of orderItems) {
        if (!item.isCompanySeedling) {
          // Create seed batch for customer seeds
          const seedBatchPayload: CreateSeedBatchRequest = {
            ownershipType: "Customer",
            customerId: formData.customerId,
            receivedDate: new Date().toISOString(),
            variety: item.variety!,
            numberOfPacks: parseInt(item.numberOfPacks ?? "1"),
            quantityPerPack: parseInt(item.quantityPerPack ?? "1"),
            unit: parseInt(item.unit),
            cropTypeId: item.cropTypeId!,
            qualityNotes: item.qualityNotes,
            storageLocation: item.storageLocation!,
            seedSource: "Customer-provided",
            lotNumber: item.lotNumber ?? "",
            packagingDate: item.packagingDate
              ? new Date(`${item.packagingDate}T00:00:00Z`).toISOString()
              : "",
            notes: item.notes,
          };

          const seedBatch = await new Promise<SeedBatch>((resolve, reject) => {
            createSeedBatchMutation.mutate(seedBatchPayload, {
              onSuccess: (data) => resolve(data),
              onError: (error) => reject(error),
            });
          });

          seedBatchIds.set(item.tempId, seedBatch.id);
        }
      }

      // Format payload — dates are optional (#19, #21); send null when blank.
      const payload = {
        specialDiscountRequest: requestSpecialDiscount ? {
          kind: specialDiscountKind, value: Number(specialDiscountValue), reason: specialDiscountReason.trim(),
        } : null,
        customerId: formData.customerId,
        expectedDeliveryDate: formData.expectedDeliveryDate
          ? new Date(formData.expectedDeliveryDate).toISOString()
          : null,
        fulfilmentMethod: formData.fulfilmentMethod,
        routeStopId: null,
        deliveryLocation: formData.fulfilmentMethod === "Delivery" ? formData.deliveryLocation.trim() : "",
        deliveryLatitude: formData.fulfilmentMethod === "Delivery" ? formData.deliveryLatitude : null,
        deliveryLongitude: formData.fulfilmentMethod === "Delivery" ? formData.deliveryLongitude : null,
        deliveryInstructions: formData.deliveryInstructions,
        deliveryContactPerson: formData.deliveryContactPerson,
        deliveryContactPhone: formData.deliveryContactPhone,
        transportRateId: formData.fulfilmentMethod === "Delivery" ? formData.transportRateId : null,
        deliveryAreaId: formData.fulfilmentMethod === "Delivery" ? formData.deliveryAreaId || null : null,
        manualTransportCost: formData.fulfilmentMethod === "Delivery" && formData.manualTransportCost.trim() !== ""
          ? Number(formData.manualTransportCost)
          : null,
        includesTrayDeposit: formData.includesTrayDeposit,
        plannedPlantingDate: formData.plannedPlantingDate
          ? new Date(formData.plannedPlantingDate).toISOString()
          : null,
        specialInstructions: formData.specialInstructions,
        notes: formData.notes,
        orderItems: orderItems.map((item) => {
          // Resolve pricing mode for this item
          const itemPricing = lookupPricing(item.varietyPricingId);
          // Order quantities follow the selected pricing basis. item.unit also carries
          // the customer's seed-receipt unit, so it must not leak into the order line.
          const commercialQuantity = commercialOrderQuantity(item.quantity, itemPricing?.pricingMode, trayTypeNumber(item.trayType));

          // Only include relevant fields based on type
          if (item.isCompanySeedling) {
            return {
              varietyPricingId: item.varietyPricingId!,
              isCompanySeedling: true,
              ...commercialQuantity,
              notes: item.notes,
              greenhouseStockEntryId: item.selectedStockEntryId || null,
            };
          } else {
            return {
              varietyPricingId: item.varietyPricingId!,
              isCompanySeedling: false,
              seedBatchId: seedBatchIds.get(item.tempId),
              ...commercialQuantity,
              notes: item.notes,
            };
          }
        }),
      };

      createOrderMutation.mutate(payload, {
        onSuccess: async () => {
          // Stock reservation is now handled automatically by the backend during order creation
          // via ReservationService.ReserveStockForOrderAsync — no need to reserve again here.
          notify.success(requestSpecialDiscount ? "Order created at the current price. Special discount awaits GM approval." : "Order created successfully!");
          handleDialogOpenChange(false);
        },
        onError: (error) => {
          const envelope = handleApiError(error, { silent: true });
          const mappedErrors = mapOrderFieldErrors(envelope.fieldErrors);
          const nextErrors =
            mappedErrors.length > 0
              ? mappedErrors
              : [makeFieldError(FORM_LEVEL_FIELD, envelope.userMessage || getErrorMessage(error), envelope.code)];

          setFieldErrors(CREATE_ORDER_FORM_ID, nextErrors);
          const firstFieldError = nextErrors.find((fieldError) => fieldError.field !== FORM_LEVEL_FIELD);
          if (firstFieldError) {
            scrollToOrderField(firstFieldError.field);
          }
        },
      });
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      notify.error(`Failed to create seed batch: ${message}`);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="w-4 h-4" />
          New Order
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-h-[90vh] w-[95vw] overflow-y-auto overflow-x-hidden sm:!max-w-[1400px] lg:w-[88vw] [&>*]:min-w-0"
      >
        <DialogHeader>
          <DialogTitle>Create New Order</DialogTitle>
          <DialogDescription>Add a new propagation order</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {fieldErrors.length > 0 && (
            <div
              role="alert"
              className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 space-y-2"
            >
              <p className="font-medium">
                {fieldErrors.some((error) => error.field !== FORM_LEVEL_FIELD)
                  ? `Please correct ${fieldErrors.filter((error) => error.field !== FORM_LEVEL_FIELD).length} field${fieldErrors.filter((error) => error.field !== FORM_LEVEL_FIELD).length === 1 ? "" : "s"} before creating the order.`
                  : "The order could not be created."}
              </p>
              <ul className="space-y-1">
                {fieldErrors.map((error, index) => (
                  <li key={`${error.field}-${index}`}>
                    {error.field === FORM_LEVEL_FIELD ? (
                      <span>{error.userMessage}</span>
                    ) : (
                      <button
                        type="button"
                        className="text-left underline-offset-2 hover:underline"
                        onClick={() => scrollToOrderField(error.field)}
                      >
                        {error.userMessage}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Customer & Dates */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Customer & Dates</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <Field data-order-field="customerId">
                <FieldLabel htmlFor="customerId">
                  Customer <span className="text-red-500">*</span>
                </FieldLabel>
                <CustomerSearchSelect
                  selectedCustomerId={formData.customerId}
                  selectedCustomerName={customerName}
                  onSelect={(customerId, name) => {
                    setSelectedSavedAddressId("");
                    setFormData((previous) => ({
                      ...previous,
                      customerId,
                      transportRateId: "",
                      deliveryAreaId: "",
                      deliveryLocation: "",
                      deliveryLatitude: null,
                      deliveryLongitude: null,
                    }));
                    setCustomerName(name);
                  }}
                  disabled={createOrderMutation.isPending}
                />
                {orderFieldError("customerId")}
              </Field>

              <Field data-order-field="plannedPlantingDate">
                <FieldLabel htmlFor="plannedPlantingDate">Planned Planting Date</FieldLabel>
                <DatePicker
                  id="plannedPlantingDate"
                  value={formData.plannedPlantingDate}
                  onChange={(value) => handleFormChange("plannedPlantingDate", value)}
                  disabled={createOrderMutation.isPending}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Optional. May be in the past for late-recorded propagations.
                </p>
                {orderFieldError("plannedPlantingDate")}
              </Field>

              <Field data-order-field="expectedDeliveryDate">
                <FieldLabel htmlFor="expectedDeliveryDate">Expected Delivery Date</FieldLabel>
                <DatePicker
                  id="expectedDeliveryDate"
                  value={formData.expectedDeliveryDate}
                  onChange={(value) => handleFormChange("expectedDeliveryDate", value)}
                  disabled={createOrderMutation.isPending}
                />
                <p className="text-xs text-muted-foreground mt-1">Optional.</p>
                {orderFieldError("expectedDeliveryDate")}
              </Field>
            </div>

          </div>

          {/* Fulfilment & destination — captured before line items so delivery intent is explicit. */}
          <div className="rounded-lg border bg-muted/20 p-4">
            <h3 className="text-lg font-semibold mb-1">Fulfilment & Destination</h3>
            <p className="mb-4 text-sm text-muted-foreground">
              Choose how this specific order will reach the customer. Delivery destinations are saved against the order.
            </p>
            {(() => {
              const totalTrayQty = orderItems
                .filter((item) => !isPerSeedlingPricing(lookupPricing(item.varietyPricingId)?.pricingMode))
                .reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);

              return (
                <>
                  <Field data-order-field="fulfilmentMethod">
                    <FieldLabel>Fulfilment method <span className="text-red-500">*</span></FieldLabel>
                    <Select
                      value={formData.fulfilmentMethod}
                      onValueChange={(fulfilmentMethod: "CustomerCollection" | "Delivery") => {
                        setSelectedSavedAddressId("");
                        setFormData((previous) => ({
                          ...previous,
                          fulfilmentMethod,
                          transportRateId: "",
                          deliveryAreaId: "",
                          manualTransportCost: "",
                          deliveryLocation: "",
                          deliveryLatitude: null,
                          deliveryLongitude: null,
                          deliveryInstructions: "",
                        }));
                      }}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CustomerCollection">Customer Collection</SelectItem>
                        <SelectItem value="Delivery">FarmSmart Delivery</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>

                  {formData.fulfilmentMethod === "Delivery" && (
                    <div className="mt-4 grid grid-cols-1 gap-4">
                      {formData.customerId && (
                        <Field>
                          <FieldLabel>Saved delivery address</FieldLabel>
                          <Select value={selectedSavedAddressId} onValueChange={(id) => {
                            setSelectedSavedAddressId(id);
                            const address = savedAddressesQuery.data?.items.find((item) => item.id === id);
                            if (address) setFormData((previous) => ({ ...previous,
                              transportRateId: address.transportRateId,
                              deliveryAreaId: address.deliveryAreaId,
                              deliveryLocation: address.address,
                              deliveryLatitude: address.latitude ?? null,
                              deliveryLongitude: address.longitude ?? null,
                            }));
                            else if (id === "none") setFormData((previous) => ({ ...previous,
                              transportRateId: "",
                              deliveryAreaId: "",
                              deliveryLocation: "",
                              deliveryLatitude: null,
                              deliveryLongitude: null,
                            }));
                          }}>
                            <SelectTrigger><SelectValue placeholder={savedAddressesQuery.isLoading ? "Loading addresses..." : "Choose saved address"} /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Enter a new address</SelectItem>
                              {(savedAddressesQuery.data?.items ?? []).map((address) => (
                                <SelectItem key={address.id} value={address.id}>
                                  {address.label} · {address.deliveryArea} · {address.address}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </Field>
                      )}
                      <Field data-order-field="transportRateId">
                        <FieldLabel>Pricing region <span className="text-red-500">*</span></FieldLabel>
                        <Select
                          value={formData.transportRateId}
                          onValueChange={(transportRateId) => {
                            setSelectedSavedAddressId("");
                            setFormData((previous) => ({ ...previous, transportRateId, deliveryAreaId: "" }));
                          }}
                          disabled={transportRatesQuery.isLoading || transportRatesQuery.isError || activeTransportRates.length === 0}
                        >
                          <SelectTrigger aria-invalid={hasFieldError("transportRateId")}>
                            <SelectValue placeholder={transportRatesQuery.isLoading ? "Loading destinations..." : "Select destination"} />
                          </SelectTrigger>
                          <SelectContent>
                            {activeTransportRates.map((rate) => (
                              <SelectItem key={rate.id} value={rate.id}>
                                {rate.destinationName} · KES {rate.costAmount.toLocaleString("en-KE")}/tray
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {orderFieldError("transportRateId")}
                      </Field>
                      {!!selectedTransportRate?.deliveryAreas?.length && (
                        <Field data-order-field="deliveryAreaId">
                          <FieldLabel>Delivery area <span className="text-red-500">*</span></FieldLabel>
                          <Select value={formData.deliveryAreaId || "none"}
                            onValueChange={(deliveryAreaId) => {
                              setSelectedSavedAddressId("");
                              setFormData((previous) => ({ ...previous, deliveryAreaId: deliveryAreaId === "none" ? "" : deliveryAreaId }));
                            }}>
                            <SelectTrigger aria-invalid={hasFieldError("deliveryAreaId")}><SelectValue placeholder="Select area" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Select area</SelectItem>
                              {selectedTransportRate.deliveryAreas.map((area) => (
                                <SelectItem key={area.id} value={area.id}>{area.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {orderFieldError("deliveryAreaId")}
                        </Field>
                      )}
                      {transportRatesQuery.isError && (
                        <div className="flex items-center justify-between gap-3 text-sm text-destructive">
                          <span>Delivery destinations could not be loaded.</span>
                          <Button type="button" variant="outline" size="sm" onClick={() => void transportRatesQuery.refetch()}>
                            Retry
                          </Button>
                        </div>
                      )}
                      {!transportRatesQuery.isLoading && !transportRatesQuery.isError && activeTransportRates.length === 0 && (
                        <p className="text-sm text-muted-foreground">
                          No active transport rates are configured. Ask an administrator to configure a delivery destination and rate.
                        </p>
                      )}
                      {selectedTransportRate && (
                        <p className="text-sm text-muted-foreground">
                          Standard charge: KES {selectedTransportRate.costAmount.toLocaleString("en-KE")}/tray × {totalTrayQty} trays = KES {(selectedTransportRate.costAmount * totalTrayQty).toLocaleString("en-KE")}
                        </p>
                      )}

                      <Field data-order-field="manualTransportCost">
                        <FieldLabel htmlFor="manualTransportCost">Transport charge override (KES) <span className="font-normal text-muted-foreground">(optional)</span></FieldLabel>
                        <Input
                          id="manualTransportCost"
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.manualTransportCost}
                          onChange={(event) => handleFormChange("manualTransportCost", event.target.value)}
                          placeholder={selectedTransportRate ? String(selectedTransportRate.costAmount * totalTrayQty) : "Enter total charge"}
                          aria-invalid={hasFieldError("manualTransportCost")}
                          disabled={createOrderMutation.isPending}
                        />
                        <p className="text-xs text-muted-foreground">Leave blank to use the tray rate. Enter a total charge for piece orders or to override the calculated amount.</p>
                        {orderFieldError("manualTransportCost")}
                      </Field>

                      <Field data-order-field="deliveryLocation">
                        <FieldLabel htmlFor="deliveryLocation">
                          Exact delivery location <span className="font-normal text-muted-foreground">(optional)</span>
                        </FieldLabel>
                        <Input
                          id="deliveryLocation"
                          value={formData.deliveryLocation}
                          aria-invalid={hasFieldError("deliveryLocation")}
                          placeholder="Farm, school, village, road, or identifiable landmark"
                          maxLength={200}
                          onChange={(event) => {
                            setSelectedSavedAddressId("");
                            setFormData((previous) => ({
                              ...previous,
                              deliveryLocation: event.target.value,
                              deliveryLatitude: null,
                              deliveryLongitude: null,
                            }));
                          }}
                          disabled={createOrderMutation.isPending}
                        />
                        {orderFieldError("deliveryLocation")}
                        {!formData.deliveryLocation.trim() && (
                          <div role="status" className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                            Confirm the exact delivery address before dispatch. A map pin is optional.
                          </div>
                        )}
                        <div className="flex flex-wrap items-center gap-2">
                          <MapLocationPicker
                            initial={formData.deliveryLatitude != null && formData.deliveryLongitude != null
                              ? { latitude: formData.deliveryLatitude, longitude: formData.deliveryLongitude }
                              : null}
                            title="Pin the exact delivery location"
                            description={`Choose the precise farm or site within ${selectedTransportRate?.destinationName ?? "the selected transport zone"}.`}
                            onConfirm={(location) => {
                              setSelectedSavedAddressId("");
                              setFormData((previous) => ({
                                ...previous,
                                deliveryLocation: location.label?.trim()
                                  || previous.deliveryLocation
                                  || `${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`,
                                deliveryLatitude: location.latitude,
                                deliveryLongitude: location.longitude,
                              }));
                            }}
                          />
                          {formData.deliveryLatitude != null && formData.deliveryLongitude != null && (
                            <span className="text-xs text-muted-foreground">
                              Pin: {formData.deliveryLatitude.toFixed(5)}, {formData.deliveryLongitude.toFixed(5)}
                            </span>
                          )}
                        </div>
                      </Field>
                      {formData.customerId && canManageAddresses && (
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                          <Field className="flex-1">
                            <FieldLabel htmlFor="save-delivery-address-label">Save this address as</FieldLabel>
                            <Input id="save-delivery-address-label" value={addressLabel}
                              onChange={(event) => setAddressLabel(event.target.value)}
                              placeholder="Home, farm, branch..." maxLength={100} />
                          </Field>
                          <Button type="button" variant="outline"
                            disabled={saveAddressMutation.isPending || !addressLabel.trim() ||
                              !formData.deliveryLocation.trim() || !formData.transportRateId ||
                              !(formData.deliveryAreaId || selectedTransportRate?.deliveryAreas?.[0]?.id)}
                            onClick={() => saveAddressMutation.mutate()}>
                            {saveAddressMutation.isPending ? "Saving…" : "Save address"}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}

            <div className="grid grid-cols-1 gap-4 mt-4 md:grid-cols-2">
              <Field data-order-field="deliveryContactPerson">
                <FieldLabel htmlFor="deliveryContactPerson">Contact Person</FieldLabel>
                <Input
                  id="deliveryContactPerson"
                  value={formData.deliveryContactPerson}
                  aria-invalid={hasFieldError("deliveryContactPerson")}
                  onChange={(event) => handleFormChange("deliveryContactPerson", event.target.value)}
                  disabled={createOrderMutation.isPending}
                />
                {orderFieldError("deliveryContactPerson")}
              </Field>

              <div data-order-field="deliveryContactPhone">
                <PhoneNumberInput
                  label="Contact Phone"
                  required={false}
                  value={formData.deliveryContactPhone}
                  onChange={(value) => handleFormChange("deliveryContactPhone", value)}
                  disabled={createOrderMutation.isPending}
                />
                {orderFieldError("deliveryContactPhone")}
              </div>

              {formData.fulfilmentMethod === "Delivery" && (
                <Field data-order-field="deliveryInstructions" className="md:col-span-2">
                  <FieldLabel htmlFor="deliveryInstructions">Delivery instructions</FieldLabel>
                  <Textarea
                    id="deliveryInstructions"
                    value={formData.deliveryInstructions}
                    placeholder="Optional, e.g. call on arrival; use the second gate"
                    maxLength={1000}
                    onChange={(event) => handleFormChange("deliveryInstructions", event.target.value)}
                    disabled={createOrderMutation.isPending}
                  />
                </Field>
              )}
            </div>
          </div>

          {/* Order Items */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Order Items</h3>

            <div className="space-y-4 mb-4 p-4 rounded border bg-muted/50">
              {/* Item Type Toggle */}
              <div>
                <p className="text-sm font-medium mb-3">Item Type</p>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant={
                      itemData.isCompanySeedling ? "default" : "outline"
                    }
                    onClick={() => {
                      setCompanySeedlingCategory("vegetable");
                      setItemData((prev) => ({
                        ...prev,
                        isCompanySeedling: true,
                        cropTypeId: "",
                        cropVarietyId: "",
                        varietyPricingId: "",
                        variety: "",
                        numberOfPacks: "",
                        quantityPerPack: "",
                        seedFormat: "packets",
                        unit: "1",
                        qualityNotes: "",
                        storageLocation: "",
                      }));
                    }}
                    disabled={createOrderMutation.isPending}
                  >
                    Company Seedling
                  </Button>
                  <Button
                    type="button"
                    variant={
                      !itemData.isCompanySeedling ? "default" : "outline"
                    }
                    onClick={() =>
                      setItemData((prev) => ({
                        ...prev,
                        isCompanySeedling: false,
                        cropTypeId: "",
                        cropVarietyId: "",
                        varietyPricingId: "",
                        variety: "",
                        quantity: "",
                        lotNumber: "",
                        packagingDate: "",
                        seedFormat: "packets",
                      }))
                    }
                    disabled={createOrderMutation.isPending}
                  >
                    Customer Seeds
                  </Button>
                </div>
              </div>

              {/* Company Seedling Path */}
              {itemData.isCompanySeedling && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="col-span-3">
                    <CompanySeedlingCategorySelector
                      category={companySeedlingCategory}
                      onCategoryChange={setCompanySeedlingCategory}
                      selectedValue={itemData.cropTypeId ?? ""}
                      selectedLabel={selectedCompanySeedlingCropTypeLabel}
                      onSelectedValueChange={(cropTypeId) => {
                        const selected = (cropTypesQuery.data ?? []).find((ct) => ct.id === cropTypeId);
                        setItemData((prev) => ({
                          ...prev,
                          cropTypeId,
                          cropVarietyId: "",
                          varietyPricingId: "",
                          variety: selected?.name ?? "",
                          selectedStockEntryId: "",
                          selectedStockAvailableQuantity: 0,
                        }));
                      }}
                      fetchOptions={fetchCompanySeedlingCropTypes}
                      disabled={createOrderMutation.isPending}
                    />
                  </div>

                  {itemData.cropTypeId && selectedCropTypeHasVarieties && (
                    <Field>
                      <FieldLabel>
                        Variety <span className="text-red-500">*</span>
                      </FieldLabel>
                      <Popover open={varietyComboOpen} onOpenChange={setVarietyComboOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={varietyComboOpen}
                            className="w-full justify-between font-normal"
                            disabled={createOrderMutation.isPending}
                          >
                            {itemData.cropVarietyId
                              ? varietiesForCropType.find((v) => v.id === itemData.cropVarietyId)?.name
                              : "Select Variety"}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[--radix-popover-trigger-width] max-w-[calc(100vw-2rem)] p-0" align="start" sideOffset={4} avoidCollisions collisionPadding={16}>
                          <Command>
                            <CommandInput placeholder="Search variety..." />
                            <CommandList>
                              <CommandEmpty>No active variety matches under {cropTypesQuery.data?.find((crop) => crop.id === itemData.cropTypeId)?.name ?? "this crop type"}.</CommandEmpty>
                              <CommandGroup>
                                {varietiesForCropType.map((variety) => {
                                  return (
                                    <CommandItem
                                      key={variety.id}
                                      value={variety.name}
                                      onSelect={() => {
                                        setItemData((prev) => ({
                                          ...prev,
                                          cropVarietyId: variety.id,
                                          varietyPricingId: "",
                                          variety: variety.name,
                                          trayType: prev.trayType,
                                          selectedStockEntryId: "",
                                          selectedStockAvailableQuantity: 0,
                                        }));
                                        setVarietyComboOpen(false);
                                      }}
                                    >
                                      <Check
                                        className={`mr-2 h-4 w-4 ${itemData.cropVarietyId === variety.id ? "opacity-100" : "opacity-0"}`}
                                      />
                                      {variety.name}
                                    </CommandItem>
                                  );
                                })}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </Field>
                  )}

                  <Field>
                    <FieldLabel>
                      {isPerSeedling ? "Quantity (Seedlings/Pieces)" : "Quantity (Trays)"} <span className="text-red-500">*</span>
                    </FieldLabel>
                    <Input
                      id="quantity"
                      type="number"
                      step="any"
                      min="0.0001"
                      placeholder="0"
                      value={itemData.quantity}
                      onChange={(e) => {
                        const newQty = e.target.value;
                        setItemData((prev) => {
                          // Deselect batch if the new quantity exceeds it
                          const exceedsBatch = prev.selectedStockEntryId &&
                            parseFloat(newQty || "0") > (prev.selectedStockAvailableQuantity ?? 0);
                          return {
                            ...prev,
                            quantity: newQty,
                            ...(exceedsBatch ? { selectedStockEntryId: "", selectedStockAvailableQuantity: 0 } : {}),
                          };
                        });
                      }}
                      disabled={createOrderMutation.isPending}
                    />
                    {physicalEquivalent && <p className={physicalEquivalent.error ? "text-sm text-destructive" : "text-sm text-muted-foreground"}>
                      {physicalEquivalent.error ?? `Equivalent: ${physicalEquivalent.pieces} seedlings (physical capacity)`}
                    </p>}
                  </Field>

                  <Field>
                    <FieldLabel>
                      Pricing Option <span className="text-red-500">*</span>
                    </FieldLabel>
                    <Select
                      value={itemData.varietyPricingId || ""}
                      onValueChange={(v) => {
                        if (v === "__load_more") { void varietiesQuery.fetchNextPage(); return; }
                        const pricing = varietiesQuery.data?.find((vp) => vp.id === v);
                        setItemData((prev) => ({
                          ...prev,
                          varietyPricingId: v,
                          trayType: pricing?.trayType ? String(pricing.trayType) : "",
                          // Drop any previously-picked stock entry — it may not match the new tray type.
                          selectedStockEntryId: "",
                          selectedStockAvailableQuantity: 0,
                        }));
                      }}
                      disabled={createOrderMutation.isPending || (!itemData.cropVarietyId && selectedCropTypeHasVarieties)}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            !itemData.cropTypeId
                              ? "Select crop type first"
                              : (!itemData.cropVarietyId && selectedCropTypeHasVarieties)
                                ? "Select variety first"
                                : varietiesQuery.isFetching
                                  ? "Loading pricing..."
                                  : "Select pricing"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {varietiesQuery.isFetching ? (
                          <div className="px-2 py-1.5 text-sm text-muted-foreground">Loading pricing...</div>
                        ) : availablePricingsForSelection.length === 0 && !varietiesQuery.hasNextPage ? (
                          <div className="px-2 py-1.5 text-sm text-muted-foreground">
                            No active pricing is configured for this variety and order type.
                          </div>
                        ) : null}
                        {availablePricingsForSelection.map((vp) => (
                          <SelectItem key={vp.id} value={vp.id}>
                            {isPerSeedlingPricing(vp.pricingMode)
                              ? `Per Seedling/Piece — KES ${vp.companySeedlingPrice?.toLocaleString() ?? "—"}/pc`
                              : `${vp.trayTypeName || vp.trayType + '-Hole Tray'} — KES ${vp.companySeedlingPrice?.toLocaleString() ?? "—"}/tray`}
                          </SelectItem>
                        ))}
                        {varietiesQuery.hasNextPage && <SelectItem value="__load_more" disabled={varietiesQuery.isFetchingNextPage}>{varietiesQuery.isFetchingNextPage ? "Loading pricing..." : "Load more pricing"}</SelectItem>}
                      </SelectContent>
                    </Select>
                    {itemData.varietyPricingId && (
                      <p className="text-xs mt-1">
                        {availableEntriesQuery.isLoading
                          ? <span className="text-muted-foreground">Loading stock…</span>
                          : (() => {
                              const readyQty = compatibleAvailableEntries
                                .filter((e) => e.isReadyForDispatch)
                                .reduce((s, e) => s + availableInOrderUnits(e, selectedPricing?.pricingMode), 0);
                              const pipelineQty = compatibleAvailableEntries
                                .filter((e) => !e.isReadyForDispatch)
                                .reduce((s, e) => s + availableInOrderUnits(e, selectedPricing?.pricingMode), 0);
                              if (readyQty > 0)
                                return <span className="text-green-600">✓ {readyQty} {stockUnitLabel} ready in stock{pipelineQty > 0 ? ` · ${pipelineQty} more in pipeline` : ""}</span>;
                              if (pipelineQty > 0)
                                return <span className="text-amber-600">⚠ {pipelineQty} {stockUnitLabel} in pipeline (not ready yet)</span>;
                              return <span className="text-red-600">✗ No stock available — sowing required</span>;
                            })()}
                      </p>
                    )}
                  </Field>
                </div>
              )}

              {/* Stock summary cards per tray type — shown after variety is selected, before pricing is chosen */}
              {itemData.isCompanySeedling && itemData.cropTypeId && (selectedCropTypeHasVarieties ? itemData.cropVarietyId : true) &&
                availablePricingsForSelection.filter((vp) => !isPerSeedlingPricing(vp.pricingMode)).length > 0 && (
                <div className="flex gap-3">
                  {availablePricingsForSelection
                    .filter((vp) => !isPerSeedlingPricing(vp.pricingMode))
                    .map((vp) => {
                      const matched = (availableEntriesQuery.data ?? []).filter((entry) =>
                        stockMatchesOrder(entry, pricingStockTarget(vp)));
                      const readyQty = matched
                        .filter((e) => e.isReadyForDispatch)
                        .reduce((s, e) => s + e.availableQuantity, 0);
                      const pipelineQty = matched
                        .filter((e) => !e.isReadyForDispatch)
                        .reduce((s, e) => s + e.availableQuantity, 0);
                      const isSelected = itemData.varietyPricingId === vp.id;

                      return (
                        <div
                          key={vp.id}
                          className={`flex-1 rounded border p-3 text-sm space-y-1 cursor-pointer transition-colors ${isSelected ? "border-primary bg-primary/5" : "bg-muted/20 hover:bg-muted/40"}`}
                          onClick={() => setItemData((prev) => ({
                            ...prev,
                            varietyPricingId: vp.id,
                            trayType: String(vp.trayType ?? ""),
                            selectedStockEntryId: "",
                            selectedStockAvailableQuantity: 0,
                          }))}
                        >
                          <p className="font-medium">{vp.trayTypeName || `${vp.trayType}-Hole Tray`}</p>
                          <p className="text-xs text-muted-foreground">KES {vp.companySeedlingPrice?.toLocaleString() ?? "—"}/tray</p>
                          {availableEntriesQuery.isLoading ? (
                            <p className="text-xs text-muted-foreground">Loading…</p>
                          ) : readyQty > 0 ? (
                            <>
                              <p className="text-xs text-green-600">✓ {readyQty} trays ready</p>
                              {pipelineQty > 0 && <p className="text-xs text-amber-600">+ {pipelineQty} in pipeline</p>}
                            </>
                          ) : pipelineQty > 0 ? (
                            <p className="text-xs text-amber-600">⚑ {pipelineQty} trays in pipeline only</p>
                          ) : (
                            <p className="text-xs text-red-600">✗ No stock — sowing needed</p>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}

              {/* Stock entries panel for company seedlings — shown after pricing is selected */}
              {itemData.isCompanySeedling && itemData.varietyPricingId && itemData.cropTypeId && (selectedCropTypeHasVarieties ? itemData.cropVarietyId : true) && (
                <div className="border rounded p-3 bg-muted/30 space-y-2">
                  <p className="text-sm font-medium">Available Stock</p>
                  {availableEntriesQuery.isLoading ? (
                    <p className="text-xs text-muted-foreground">Loading stock entries…</p>
                  ) : !compatibleAvailableEntries.length ? (
                    <p className="text-xs text-amber-600">No stock available — a sowing instruction will be needed</p>
                  ) : (() => {
                    const readyEntries = compatibleAvailableEntries.filter(e => e.isReadyForDispatch);
                    const pipelineEntries = compatibleAvailableEntries.filter(e => !e.isReadyForDispatch);
                    return (
                      <div className="space-y-3">
                        {/* Ready stock */}
                        {readyEntries.length > 0 && (
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-green-700">Ready for dispatch ({readyEntries.length})</p>
                            <div
                              className={`flex items-center gap-2 p-2 rounded border cursor-pointer text-xs ${!itemData.selectedStockEntryId ? "border-primary bg-primary/5" : "border-transparent"}`}
                              onClick={() => setItemData((p) => ({ ...p, selectedStockEntryId: "", selectedStockAvailableQuantity: 0 }))}
                            >
                              <div className={`w-3 h-3 rounded-full border-2 flex-shrink-0 ${!itemData.selectedStockEntryId ? "bg-primary border-primary" : "border-muted-foreground"}`} />
                              <span className="text-muted-foreground">System reserve — auto-allocate from stock or queue for sowing</span>
                            </div>
                            <p className="text-xs text-muted-foreground pl-5">
                              The system will allocate from available batches at approval, or create a sowing instruction if stock is insufficient.
                            </p>
                            {readyEntries.map((entry) => {
                              const requestedQty = parseFloat(itemData.quantity || "0");
                              const availableQty = availableInOrderUnits(entry, selectedPricing?.pricingMode);
                              const insufficient = requestedQty > 0 && availableQty < requestedQty;
                              const isSelected = itemData.selectedStockEntryId === entry.entryId;
                              return (
                              <div
                                key={entry.entryId}
                                className={`flex items-center gap-2 p-2 rounded border text-xs ${
                                  insufficient
                                    ? "opacity-50 cursor-not-allowed border-transparent"
                                    : isSelected
                                      ? "border-primary bg-primary/5 cursor-pointer"
                                      : "border-transparent hover:bg-muted cursor-pointer"
                                }`}
                                onClick={() => {
                                  if (!insufficient) {
                                    setItemData((p) => ({ ...p, selectedStockEntryId: entry.entryId, selectedStockAvailableQuantity: availableQty }));
                                  }
                                }}
                              >
                                <div className={`w-3 h-3 rounded-full border-2 flex-shrink-0 ${isSelected ? "bg-primary border-primary" : "border-muted-foreground"}`} />
                                <div>
                                  <span className="font-medium">{availableQty} {stockUnitLabel} available</span>
                                  {insufficient && <span className="ml-1 text-red-500 font-medium">— insufficient for {requestedQty}</span>}
                                  {entry.source === "Agroforestry" && <span className="ml-1 text-teal-600 font-medium">(Agroforestry)</span>}
                                  {entry.greenhouseName && <span className="text-muted-foreground"> · {entry.greenhouseName}{entry.chamberName ? ` / ${entry.chamberName}` : ""}</span>}
                                  {entry.trayType && <span className="text-muted-foreground"> · {entry.trayType}</span>}
                                  {entry.sowingDate && <span className="text-muted-foreground"> · sown {formatDateSafe(entry.sowingDate)}</span>}
                                  <span className="text-muted-foreground"> · entered {formatDateSafe(entry.entryDate)}</span>
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Pipeline stock (healing / not ready) */}
                        {pipelineEntries.length > 0 && (
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-amber-600">In pipeline — not yet ready ({pipelineEntries.length})</p>
                            {pipelineEntries.map((entry) => (
                              <div
                                key={entry.entryId}
                                className="flex items-center gap-2 p-2 rounded text-xs text-muted-foreground"
                              >
                                <div className="w-3 h-3 rounded-full border-2 border-amber-400 flex-shrink-0" />
                                <div>
                                  <span>{availableInOrderUnits(entry, selectedPricing?.pricingMode)} {stockUnitLabel}</span>
                                  {entry.source === "Agroforestry" && <span className="ml-1 text-teal-600">(Agroforestry · healing)</span>}
                                  {entry.greenhouseName && <span> · {entry.greenhouseName}{entry.chamberName ? ` / ${entry.chamberName}` : ""}</span>}
                                  {entry.expectedReadyDate && <span className="text-amber-600"> · expected ready {formatDateSafe(entry.expectedReadyDate)}</span>}
                                  {entry.sowingDate && <span> · sown {formatDateSafe(entry.sowingDate)}</span>}
                                  <span> · entered {formatDateSafe(entry.entryDate)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {readyEntries.length === 0 && pipelineEntries.length > 0 && (
                          <p className="text-xs text-amber-600">No ready stock — only pipeline batches shown above. A sowing instruction may be needed.</p>
                        )}

                        {readyEntries.length === 0 && pipelineEntries.length === 0 && (
                          <p className="text-xs text-muted-foreground">No stock entries available.</p>
                        )}

                        {itemData.selectedStockEntryId && (
                          <p className="text-xs text-green-700 pt-1">✓ This stock will be reserved when the order is created</p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Customer Seeds Path */}
              {!itemData.isCompanySeedling && (
                <div>
                  <p className="text-sm font-medium mb-3 text-blue-600">
                    Record Customer Seed Information
                  </p>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
                    <Field>
                      <FieldLabel htmlFor="lotNumber">
                        Lot No <span className="text-red-500">*</span>{" "}
                        <span className="text-muted-foreground font-normal text-xs">(printed on the seed packet)</span>
                      </FieldLabel>
                      <Input
                        id="lotNumber"
                        placeholder="e.g. LOT-2026-A3"
                        value={itemData.lotNumber ?? ""}
                        onChange={(e) =>
                          setItemData((prev) => ({
                            ...prev,
                            lotNumber: e.target.value,
                          }))
                        }
                        disabled={createOrderMutation.isPending}
                        required
                      />
                    </Field>

                    <Field>
                      <FieldLabel htmlFor="packagingDate">
                        Packaging Date <span className="text-red-500">*</span>
                      </FieldLabel>
                      <DatePicker
                        id="packagingDate"
                        value={itemData.packagingDate ?? ""}
                        onChange={(v) =>
                          setItemData((prev) => ({
                            ...prev,
                            packagingDate: v,
                          }))
                        }
                        max={new Date().toISOString().slice(0, 10)}
                        disabled={createOrderMutation.isPending}
                        required
                      />
                    </Field>

                    {/* Crop Type selector */}
                    <Field>
                      <FieldLabel>
                        Crop Type <span className="text-red-500">*</span>
                      </FieldLabel>
                      <Popover open={cropTypeComboOpenCS} onOpenChange={setCropTypeComboOpenCS}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={cropTypeComboOpenCS}
                            className="w-full justify-between font-normal"
                            disabled={createOrderMutation.isPending}
                          >
                            {itemData.cropTypeId
                              ? cropTypesQuery.data?.find((ct) => ct.id === itemData.cropTypeId)?.name
                              : "Select Crop Type"}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[--radix-popover-trigger-width] max-w-[calc(100vw-2rem)] p-0" align="start" sideOffset={4} avoidCollisions collisionPadding={16}>
                          <Command>
                            <CommandInput placeholder="Search crop type..." />
                            <CommandList>
                              <CommandEmpty>No crop type found.</CommandEmpty>
                              <CommandGroup>
                                {cropTypesQuery.data?.map((ct) => (
                                  <CommandItem
                                    key={ct.id}
                                    value={ct.name}
                                    onSelect={() => {
                                      setItemData((prev) => ({
                                        ...prev,
                                        cropTypeId: ct.id,
                                        cropVarietyId: "",
                                        varietyPricingId: "",
                                        variety: ct.name,
                                      }));
                                      setCropTypeComboOpenCS(false);
                                    }}
                                  >
                                    <Check
                                      className={`mr-2 h-4 w-4 ${itemData.cropTypeId === ct.id ? "opacity-100" : "opacity-0"}`}
                                    />
                                    {ct.name}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </Field>

                    {/* Variety selector — only if crop type has varieties */}
                    {itemData.cropTypeId && selectedCropTypeHasVarieties && (
                      <Field>
                        <FieldLabel>
                          Variety <span className="text-red-500">*</span>
                        </FieldLabel>
                        <Popover open={varietyComboOpenCS} onOpenChange={setVarietyComboOpenCS}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={varietyComboOpenCS}
                              className="w-full justify-between font-normal"
                              disabled={createOrderMutation.isPending}
                            >
                              {itemData.cropVarietyId
                                ? varietiesForCropType.find((v) => v.id === itemData.cropVarietyId)?.name
                                : "Select Variety"}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[--radix-popover-trigger-width] max-w-[calc(100vw-2rem)] p-0" align="start" sideOffset={4} avoidCollisions collisionPadding={16}>
                            <Command>
                              <CommandInput placeholder="Search variety..." />
                              <CommandList>
                                <CommandEmpty>No active variety matches under {cropTypesQuery.data?.find((crop) => crop.id === itemData.cropTypeId)?.name ?? "this crop type"}.</CommandEmpty>
                                <CommandGroup>
                                  {varietiesForCropType.map((variety) => {
                                    return (
                                      <CommandItem
                                        key={variety.id}
                                        value={variety.name}
                                        onSelect={() => {
                                          setItemData((prev) => ({
                                            ...prev,
                                            cropVarietyId: variety.id,
                                            varietyPricingId: "",
                                            variety: variety.name,
                                            trayType: prev.trayType,
                                          }));
                                          setVarietyComboOpenCS(false);
                                        }}
                                      >
                                        <Check
                                          className={`mr-2 h-4 w-4 ${itemData.cropVarietyId === variety.id ? "opacity-100" : "opacity-0"}`}
                                        />
                                        {variety.name}
                                      </CommandItem>
                                    );
                                  })}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </Field>
                    )}

                    {/* Seed Format toggle */}
                    <div className="col-span-2">
                      <p className="text-sm font-medium mb-2">Seed Format</p>
                      <div className="flex gap-3">
                        <Button
                          type="button"
                          size="sm"
                          variant={itemData.seedFormat === "packets" ? "default" : "outline"}
                          onClick={() =>
                            setItemData((prev) => ({
                              ...prev,
                              seedFormat: "packets",
                              numberOfPacks: "",
                              quantityPerPack: "",
                              unit: "1",
                            }))
                          }
                          disabled={createOrderMutation.isPending}
                        >
                          Seed Packets
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={itemData.seedFormat === "pieces" ? "default" : "outline"}
                          onClick={() =>
                            setItemData((prev) => ({
                              ...prev,
                              seedFormat: "pieces",
                              numberOfPacks: "",
                              quantityPerPack: "",
                              unit: "5", // Pieces
                            }))
                          }
                          disabled={createOrderMutation.isPending}
                        >
                          Loose / Pieces
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {itemData.seedFormat === "packets"
                          ? "Seeds packaged in sachets/packs (e.g. 3 packs × 500 seeds)"
                          : "Individual seeds, cuttings, or tubers counted as pieces (e.g. 10 avocado seeds, 50 potato vines)"}
                      </p>
                    </div>

                    {/* Packets: Number of Packs + Quantity in Pack + Unit */}
                    {itemData.seedFormat === "packets" && (
                      <>
                        <Field>
                          <FieldLabel htmlFor="numberOfPacks">
                            Number of Packs <span className="text-red-500">*</span>
                          </FieldLabel>
                          <Input
                            id="numberOfPacks"
                            type="number"
                            step="1"
                            min="1"
                            placeholder="e.g. 3"
                            value={itemData.numberOfPacks}
                            onChange={(e) =>
                              setItemData((prev) => ({
                                ...prev,
                                numberOfPacks: e.target.value,
                              }))
                            }
                            disabled={createOrderMutation.isPending}
                          />
                        </Field>

                        <Field>
                          <FieldLabel htmlFor="quantityPerPack">
                            Quantity in Pack <span className="text-red-500">*</span>
                          </FieldLabel>
                          <Input
                            id="quantityPerPack"
                            type="number"
                            step="1"
                            min="1"
                            placeholder="e.g. 500"
                            value={itemData.quantityPerPack}
                            onChange={(e) =>
                              setItemData((prev) => ({
                                ...prev,
                                quantityPerPack: e.target.value,
                              }))
                            }
                            disabled={createOrderMutation.isPending}
                          />
                        </Field>

                        <Field>
                          <FieldLabel htmlFor="unit">
                            Unit <span className="text-red-500">*</span>
                          </FieldLabel>
                          <Select
                            value={itemData.unit}
                            onValueChange={(v) =>
                              setItemData((prev) => ({ ...prev, unit: v }))
                            }
                            disabled={createOrderMutation.isPending}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select unit" />
                            </SelectTrigger>
                            <SelectContent>
                              {unitsQuery.data
                                ?.filter((u) => ["kg", "g", "kgs", "grams"].includes(u.abbreviation.toLowerCase()) || ["kilograms", "grams"].includes(u.name.toLowerCase()))
                                .map((u) => (
                                  <SelectItem key={u.value} value={String(u.value)}>
                                    {u.name}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </Field>
                      </>
                    )}

                    {/* Pieces: just a quantity of pieces */}
                    {itemData.seedFormat === "pieces" && (
                      <Field className="col-span-2">
                        <FieldLabel htmlFor="pieceCount">
                          Number of Seeds / Pieces <span className="text-red-500">*</span>
                        </FieldLabel>
                        <Input
                          id="pieceCount"
                          type="number"
                          step="1"
                          min="1"
                          placeholder="e.g. 10"
                          value={itemData.quantityPerPack}
                          onChange={(e) =>
                            setItemData((prev) => ({
                              ...prev,
                              quantityPerPack: e.target.value,
                            }))
                          }
                          disabled={createOrderMutation.isPending}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          How many individual seeds, cuttings, or tubers the customer brought
                        </p>
                      </Field>
                    )}

                    <Field>
                      <FieldLabel htmlFor="storageLocation">
                        Storage Location <span className="text-red-500">*</span>
                      </FieldLabel>
                      <Input
                        id="storageLocation"
                        placeholder="e.g., Cold room A, Shelf B3"
                        value={itemData.storageLocation}
                        onChange={(e) =>
                          setItemData((prev) => ({
                            ...prev,
                            storageLocation: e.target.value,
                          }))
                        }
                        disabled={createOrderMutation.isPending}
                      />
                    </Field>

                    {/* Pricing Option and Estimated Quantity — billing fields */}
                    <Field>
                      <FieldLabel htmlFor="customerPricingOption">
                        Pricing Option <span className="text-red-500">*</span>
                      </FieldLabel>
                      <Select
                        value={itemData.varietyPricingId || ""}
                        onValueChange={(v) => {
                          if (v === "__load_more") { void varietiesQuery.fetchNextPage(); return; }
                          const pricing = varietiesQuery.data?.find((vp) => vp.id === v);
                          setItemData((prev) => ({
                            ...prev,
                            varietyPricingId: v,
                            trayType: pricing?.trayType ? String(pricing.trayType) : "",
                          }));
                        }}
                        disabled={createOrderMutation.isPending || (!itemData.cropVarietyId && selectedCropTypeHasVarieties)}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              !itemData.cropTypeId
                                ? "Select crop type first"
                                : (!itemData.cropVarietyId && selectedCropTypeHasVarieties)
                                  ? "Select variety first"
                                  : varietiesQuery.isFetching
                                    ? "Loading pricing..."
                                    : "Select pricing"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {varietiesQuery.isFetching ? (
                            <div className="px-2 py-1.5 text-sm text-muted-foreground">Loading pricing...</div>
                          ) : availablePricingsForSelection.length === 0 && !varietiesQuery.hasNextPage ? (
                            <div className="px-2 py-1.5 text-sm text-muted-foreground">
                              No active pricing is configured for this variety and order type.
                            </div>
                          ) : null}
                          {availablePricingsForSelection.map((vp) => (
                            <SelectItem key={vp.id} value={vp.id}>
                              {isPerSeedlingPricing(vp.pricingMode)
                                ? `Per Seedling/Piece — KES ${vp.propagationServicePrice?.toLocaleString() ?? "—"}/pc`
                                : `${vp.trayTypeName || vp.trayType + '-Hole Tray'} — KES ${vp.propagationServicePrice?.toLocaleString() ?? "—"}/tray`}
                            </SelectItem>
                          ))}
                          {varietiesQuery.hasNextPage && <SelectItem value="__load_more" disabled={varietiesQuery.isFetchingNextPage}>{varietiesQuery.isFetchingNextPage ? "Loading pricing..." : "Load more pricing"}</SelectItem>}
                        </SelectContent>
                      </Select>
                    </Field>

                    <Field>
                      <FieldLabel htmlFor="estimatedTrays">
                        {isPerSeedling ? "Estimated Seedlings/Pieces" : "Estimated Trays"} <span className="text-red-500">*</span>
                        <span className="text-muted-foreground font-normal text-xs ml-1">(billed at deposit, adjusted after sowing)</span>
                      </FieldLabel>
                      <Input
                        id="estimatedTrays"
                        type="number"
                        step={isPerSeedling ? "1" : "any"}
                        min="0.0001"
                        placeholder="e.g. 50"
                        value={itemData.quantity}
                        onChange={(e) =>
                          setItemData((prev) => ({
                            ...prev,
                            quantity: e.target.value,
                          }))
                        }
                        disabled={createOrderMutation.isPending}
                      />
                    </Field>

                    {physicalEquivalent && !itemData.isCompanySeedling && <p className={physicalEquivalent.error ? "text-sm text-destructive" : "text-sm text-muted-foreground"}>
                      {physicalEquivalent.error ?? `Equivalent: ${physicalEquivalent.pieces} seedlings (physical capacity)`}
                    </p>}
                    {itemData.varietyPricingId && itemData.quantity && (() => {
                      const pricing = varietiesQuery.data?.find((v) => v.id === itemData.varietyPricingId);
                      if (!pricing || pricing.propagationServicePrice == null) return null;
                      const qty = parseFloat(itemData.quantity);
                      const price = pricing.propagationServicePrice;
                      const subtotal = qty * price;
                      const unitLabel = isPerSeedlingPricing(pricing.pricingMode) ? "pcs" : "trays";
                      return (
                        <div className="col-span-2 p-3 rounded border bg-blue-950/30 border-blue-800/30 text-sm">
                          <span className="text-muted-foreground">Estimated billing: </span>
                          <span className="font-medium">{qty} {unitLabel} × KES {price.toLocaleString()} = </span>
                          <span className="font-bold text-blue-400">KES {subtotal.toLocaleString()}</span>
                          <span className="text-xs text-muted-foreground ml-2">(will be adjusted to actuals after sowing)</span>
                        </div>
                      );
                    })()}

                    <Field className="col-span-2">
                      <FieldLabel htmlFor="qualityNotes">
                        Quality Notes (Optional)
                      </FieldLabel>
                      <Textarea
                        id="qualityNotes"
                        placeholder="Observations about seed quality, germination look, etc."
                        value={itemData.qualityNotes}
                        onChange={(e) =>
                          setItemData((prev) => ({
                            ...prev,
                            qualityNotes: e.target.value,
                          }))
                        }
                        disabled={createOrderMutation.isPending}
                      />
                    </Field>
                  </div>
                </div>
              )}

              {/* Notes */}
              <Field>
                <FieldLabel htmlFor="itemNotes">Notes</FieldLabel>
                <Textarea
                  id="itemNotes"
                  placeholder="Additional item notes (optional)"
                  value={itemData.notes}
                  onChange={(e) =>
                    setItemData((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  disabled={createOrderMutation.isPending}
                />
              </Field>

              {/* Add Item Button */}
              <Button
                type="button"
                onClick={handleAddItem}
                disabled={createOrderMutation.isPending}
                className="w-full gap-2"
              >
                <Plus className="w-4 h-4" />
                Add to Order
              </Button>
            </div>

            {/* Items Table */}
              <div
                className={`rounded border overflow-x-auto ${hasFieldError("orderItems") ? "border-red-400" : ""}`}
                data-order-field="orderItems"
              >
                <Table className="min-w-[640px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead>Pricing</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orderItems.map((item, index) => {
                      const currentItemErrors = itemErrorFields(index);

                      return (
                        <TableRow
                          key={item.tempId}
                          data-order-field={`orderItems.${index}`}
                          className={currentItemErrors.length > 0 ? "border-red-300 bg-red-50/40" : undefined}
                        >
                          <TableCell className="text-sm">
                            {item.isCompanySeedling ? "Company" : "Customer"}
                          </TableCell>
                          <TableCell className="text-sm" data-order-field={`orderItems.${index}.cropTypeId`}>
                            <div>{(() => {
                              const ct = cropTypesQuery.data?.find((t) => t.id === item.cropTypeId);
                              const ctName = ct?.name || "";
                              return item.variety && item.variety !== ctName
                                ? `${ctName} › ${item.variety}`
                                : ctName || item.variety || "—";
                            })()}</div>
                            {item.isCompanySeedling && item.varietyPricingId && item.quantity && (() => {
                              const status = getStockStatus(item.varietyPricingId, parseFloat(item.quantity));
                              const needsSowing = status?.variant === "destructive";
                              return (
                                <>
                                  {status && (
                                    <Badge variant={status.variant} className="text-xs mt-1">
                                      {status.label}
                                    </Badge>
                                  )}
                                  {needsSowing && (
                                    <div className="mt-2 space-y-1">
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        className="gap-1 text-xs h-7"
                                        onClick={() => handleCalculateStageForItem(item)}
                                        disabled={calculatingItems.has(item.tempId) || !formData.plannedPlantingDate}
                                      >
                                        <Calculator className="w-3 h-3" />
                                        {calculatingItems.has(item.tempId) ? "Calculating..." : "Calculate Stage"}
                                      </Button>
                                      {!formData.plannedPlantingDate && (
                                        <p className="text-xs text-amber-600">Set Planned Planting Date first</p>
                                      )}
                                      {stageResults[item.tempId] && (
                                        <div className="p-2 bg-green-50 rounded border border-green-200 text-xs">
                                          <p className="font-medium text-green-900">Recommended: {stageResults[item.tempId].recommendedStage}</p>
                                          <p className="text-green-700 mt-0.5">{stageResults[item.tempId].reasoning}</p>
                                          <p className="text-green-600 mt-1">
                                            <span className="font-medium">Delivery:</span>{" "}
                                            {new Date(stageResults[item.tempId].recommendedDeliveryDate).toLocaleDateString()}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                            {!item.isCompanySeedling && (
                              <>
                                {item.storageLocation && (
                                  <div className="text-xs text-gray-500">Location: {item.storageLocation}</div>
                                )}
                                <div className="mt-2 space-y-1">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="gap-1 text-xs h-7"
                                    onClick={() => handleCalculateStageForItem(item)}
                                    disabled={calculatingItems.has(item.tempId) || !formData.plannedPlantingDate}
                                  >
                                    <Calculator className="w-3 h-3" />
                                    {calculatingItems.has(item.tempId) ? "Calculating..." : "Calculate Stage"}
                                  </Button>
                                  {!formData.plannedPlantingDate && (
                                    <p className="text-xs text-amber-600">Set Planned Planting Date first</p>
                                  )}
                                  {stageResults[item.tempId] && (
                                    <div className="p-2 bg-green-50 rounded border border-green-200 text-xs">
                                      <p className="font-medium text-green-900">Recommended: {stageResults[item.tempId].recommendedStage}</p>
                                      <p className="text-green-700 mt-0.5">{stageResults[item.tempId].reasoning}</p>
                                      <p className="text-green-600 mt-1">
                                        <span className="font-medium">Delivery:</span>{" "}
                                        {new Date(stageResults[item.tempId].recommendedDeliveryDate).toLocaleDateString()}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                            {currentItemErrors
                              .filter((error) => error.field.endsWith(".cropTypeId") || error.field.endsWith(".varietyId"))
                              .map((error, errorIndex) => (
                                <p key={errorIndex} className="text-red-600 text-xs mt-1">{error.userMessage}</p>
                              ))}
                          </TableCell>
                          <TableCell className="text-sm" data-order-field={`orderItems.${index}.pricingOptionId`}>
                            {(() => {
                              const itemPricing = lookupPricing(item.varietyPricingId);
                              return isPerSeedlingPricing(itemPricing?.pricingMode)
                                ? "Per Seedling/Piece"
                                : item.trayType === "442" ? "442-Hole" : "210-Hole";
                            })()}
                            {currentItemErrors
                              .filter((error) => error.field.endsWith(".pricingOptionId"))
                              .map((error, errorIndex) => (
                                <p key={errorIndex} className="text-red-600 text-xs mt-1">{error.userMessage}</p>
                              ))}
                          </TableCell>
                          <TableCell className="text-sm" data-order-field={`orderItems.${index}.quantity`}>
                            {(() => {
                              const itemPricing = lookupPricing(item.varietyPricingId);
                              const unitLabel = isPerSeedlingPricing(itemPricing?.pricingMode) ? "pcs" : "trays";
                              if (item.isCompanySeedling) {
                                return `${item.quantity} ${unitLabel}`;
                              }
                              return (
                                <div>
                                  <div className="font-medium">Est. {item.quantity} {unitLabel}</div>
                                  {item.seedFormat === "pieces"
                                    ? <div className="text-xs text-muted-foreground">{item.quantityPerPack} pieces</div>
                                    : <div className="text-xs text-muted-foreground">{item.numberOfPacks} packs × {item.quantityPerPack} seeds</div>
                                  }
                                </div>
                              );
                            })()}
                            {currentItemErrors
                              .filter((error) => error.field.endsWith(".quantity"))
                              .map((error, errorIndex) => (
                                <p key={errorIndex} className="text-red-600 text-xs mt-1">{error.userMessage}</p>
                              ))}
                          </TableCell>
                          <TableCell>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveItem(item.tempId)}
                              disabled={createOrderMutation.isPending}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {orderItems.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-3">No items added yet. Fill in the fields above and click "Add to Order".</p>
              )}
              {orderFieldError("orderItems")}

              {/* Tray deposit opt-in — shown when any PerTray items exist */}
              {orderItems.some((item) => isPerTrayPricing(lookupPricing(item.varietyPricingId)?.pricingMode) || item.trayType) && (
                <Field className="mt-4 rounded border bg-muted/30 p-3">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="includesTrayDeposit"
                      checked={formData.includesTrayDeposit}
                      onCheckedChange={(checked) =>
                        setFormData((prev) => ({
                          ...prev,
                          includesTrayDeposit: checked === true,
                        }))
                      }
                      disabled={createOrderMutation.isPending}
                    />
                    <FieldLabel htmlFor="includesTrayDeposit" className="font-normal cursor-pointer">
                      Include refundable tray deposit
                    </FieldLabel>
                  </div>
                  {formData.includesTrayDeposit && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Refundable deposit charged for whole physical trays only; loose seedlings carry no tray deposit (210-hole: KES {trayRates.rate210Hole.toLocaleString("en-KE", { minimumFractionDigits: 2 })}, 442-hole: KES {trayRates.rate442Hole.toLocaleString("en-KE", { minimumFractionDigits: 2 })}). Refunded when the storekeeper approves tray returns.
                    </p>
                  )}
                </Field>
              )}
          </div>

          {/* Additional Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Additional Info</h3>
            <Field>
              <FieldLabel>Special Instructions</FieldLabel>
              <Textarea
                value={formData.specialInstructions}
                onChange={(e) =>
                  handleFormChange("specialInstructions", e.target.value)
                }
                placeholder="Any special instructions"
                disabled={createOrderMutation.isPending}
              />
            </Field>

            <Field className="mt-4">
              <FieldLabel>Notes</FieldLabel>
              <Textarea
                value={formData.notes}
                onChange={(e) => handleFormChange("notes", e.target.value)}
                placeholder="Additional notes"
                disabled={createOrderMutation.isPending}
              />
            </Field>
          </div>

          {/* Order Total Summary */}
          {orderItems.length > 0 && (() => {
            const summary = computeOrderSummary();
            return (
              <div className="rounded border bg-muted/30 p-4 space-y-2">
                <h3 className="text-sm font-semibold">Order Total</h3>
                <div className="space-y-1 text-sm">
                  {summary.items.map(({ item, price, qty, subtotal, unitLabel }) => {
                    const ct = cropTypesQuery.data?.find((t) => t.id === item.cropTypeId);
                    const ctName = ct?.name || "";
                    const label = item.variety && item.variety !== ctName
                      ? `${ctName} › ${item.variety}`
                      : ctName || item.variety || "—";
                    return (
                      <div key={item.tempId} className="flex justify-between text-muted-foreground">
                        <span>{label}</span>
                        <span>
                          {qty} {unitLabel}
                          {price != null ? ` × KES ${price.toLocaleString("en-KE", { minimumFractionDigits: 2 })} = ` : " = "}
                          <span className="text-foreground font-medium">
                            {subtotal != null ? `KES ${subtotal.toLocaleString("en-KE", { minimumFractionDigits: 2 })}` : "—"}
                          </span>
                        </span>
                      </div>
                    );
                  })}
                  {summary.discountAmount > 0 && (
                    <div className="flex justify-between text-emerald-700">
                      <span>
                        Discount{summary.discountLabel ? ` (${summary.discountLabel})` : ""}
                        {summary.discountPct > 0 ? ` ${summary.discountPct.toFixed(2)}%` : ""}
                      </span>
                      <span className="font-medium">− KES {summary.discountAmount.toLocaleString("en-KE", { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  {summary.discountWarning && (
                    <p className="text-xs text-amber-600">{summary.discountWarning}</p>
                  )}
                  {formData.customerId && <div className="space-y-2 rounded-md border p-3">
                    <p className="font-medium">Approved discounts</p>
                    {discountQuery.isLoading && <p className="text-muted-foreground">Checking discounts…</p>}
                    {(discountQuery.data?.candidates ?? []).length === 0 && !discountQuery.isLoading && <p className="text-muted-foreground">No approved discount currently applies.</p>}
                    {(discountQuery.data?.candidates ?? []).map((candidate, index) => <div key={`${candidate.source}-${candidate.discountId ?? index}`} className="flex items-center justify-between gap-2">
                      <span>{candidate.name} · {candidate.percentage}% {candidate.selected && <Badge>Applied</Badge>}</span>
                      <span className="text-xs text-muted-foreground">{candidate.selected ? "Selected by precedence" : "Available; higher tier applied"}</span>
                    </div>)}
                    <p className="text-xs text-muted-foreground">{discountQuery.data?.precedenceExplanation}</p>
                    {canRequestDiscount && <Button type="button" size="sm" variant="outline" onClick={() => setRequestSpecialDiscount(value => !value)}>
                      {requestSpecialDiscount ? "Remove special request" : "Request Special Discount"}
                    </Button>}
                    {requestSpecialDiscount && <div className="space-y-3 border-t pt-3">
                      <p className="text-xs text-amber-700">The order will use the current price until the GM approves this request. Payment and invoice issue will wait.</p>
                      <Field><FieldLabel>Discount type</FieldLabel>
                        <Select value={specialDiscountKind} onValueChange={value => setSpecialDiscountKind(value as "Percentage" | "FixedAmount")}>
                          <SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Percentage">Percentage</SelectItem><SelectItem value="FixedAmount">Fixed amount</SelectItem></SelectContent>
                        </Select>
                      </Field>
                      <Field><FieldLabel>Requested {specialDiscountKind === "Percentage" ? "percentage" : "amount (KES)"}</FieldLabel>
                        <Input type="number" min="0" step="0.01" value={specialDiscountValue} onChange={event => setSpecialDiscountValue(event.target.value)} />
                      </Field>
                      <Field><FieldLabel>Reason</FieldLabel><Textarea value={specialDiscountReason} onChange={event => setSpecialDiscountReason(event.target.value)} /></Field>
                      {Number(specialDiscountValue) > 0 && <div className="space-y-1 text-xs">
                        <p>Current payable goods: KES {(summary.itemsSubtotal - summary.discountAmount).toFixed(2)}</p>
                        <p>Proposed payable goods: KES {(summary.itemsSubtotal - (specialDiscountKind === "Percentage" ? Math.round(summary.itemsSubtotal * Number(specialDiscountValue)) / 100 : Number(specialDiscountValue))).toFixed(2)}</p>
                        <p>Additional reduction: KES {((specialDiscountKind === "Percentage" ? Math.round(summary.itemsSubtotal * Number(specialDiscountValue)) / 100 : Number(specialDiscountValue)) - summary.discountAmount).toFixed(2)}</p>
                      </div>}
                    </div>}
                  </div>}
                  {summary.transportCost > 0 && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>Transport</span>
                      <span className="text-foreground font-medium">KES {summary.transportCost.toLocaleString("en-KE", { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  {summary.trayDeposit > 0 && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>Refundable Tray Deposit</span>
                      <span className="text-foreground font-medium">KES {summary.trayDeposit.toLocaleString("en-KE", { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-base border-t pt-2 mt-1">
                    <span>Grand Total</span>
                    <span>KES {summary.grandTotal.toLocaleString("en-KE", { minimumFractionDigits: 2 })}</span>
                  </div>
                  {showPointsEstimate && (
                    <div className="pt-1">
                      <div className="flex justify-between items-baseline text-sm text-muted-foreground">
                        <span>Loyalty points earned (est.)</span>
                        <span className="font-medium text-foreground">
                          {pointsEstimate!.points.toLocaleString("en-KE")} pts
                        </span>
                      </div>
                      <p className="text-[11px] text-muted-foreground">Estimate — credited when the order is delivered.</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Action Buttons — sticky so they never scroll away on tablets */}
          <div className="sticky bottom-0 -mx-1 flex gap-2 justify-end border-t bg-background/95 px-1 py-3 backdrop-blur">
            <Button
              variant="secondary"
              onClick={() => handleDialogOpenChange(false)}
              disabled={createOrderMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleOpenConfirm}
              disabled={createOrderMutation.isPending}
            >
              {createOrderMutation.isPending ? "Creating..." : "Create Order"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Confirmation Dialog */}
    <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Confirm Order</DialogTitle>
          <DialogDescription>Review the order details before creating</DialogDescription>
        </DialogHeader>
        {(() => {
          const summary = computeOrderSummary();
          return (
            <div className="space-y-4">
              <div className="text-sm space-y-1">
                <p><span className="text-muted-foreground">Customer:</span> <span className="font-medium">{customerName}</span></p>
                <p><span className="text-muted-foreground">Fulfilment:</span> {formData.fulfilmentMethod === "Delivery" ? `FarmSmart Delivery · ${selectedTransportRate?.destinationName ?? ""}` : "Customer Collection"}</p>
                {formData.expectedDeliveryDate && <p><span className="text-muted-foreground">Expected:</span> {new Date(formData.expectedDeliveryDate).toLocaleDateString()}</p>}
              </div>

              <div className="rounded border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Unit Price</TableHead>
                      <TableHead className="text-right">Subtotal</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summary.items.map(({ item, pricing, price, qty, subtotal, unitLabel }) => {
                      const ct = cropTypesQuery.data?.find((t) => t.id === item.cropTypeId);
                      const ctName = ct?.name || "";
                      const label = item.variety && item.variety !== ctName
                        ? `${ctName} › ${item.variety}`
                        : ctName || item.variety || "—";
                      return (
                        <TableRow key={item.tempId}>
                          <TableCell className="text-sm">
                            <div>{label}</div>
                            <div className="text-xs text-muted-foreground">
                              {item.isCompanySeedling ? "Company seedling" : "Customer seeds"}
                              {pricing && ` · ${pricing.trayTypeName || (pricing.trayType ? `${pricing.trayType}-Hole` : "")}`}
                            </div>
                          </TableCell>
                          <TableCell className="text-right text-sm">{qty} {unitLabel}</TableCell>
                          <TableCell className="text-right text-sm">
                            {price != null ? `KES ${price.toLocaleString("en-KE", { minimumFractionDigits: 2 })}` : "—"}
                          </TableCell>
                          <TableCell className="text-right text-sm font-medium">
                            {subtotal != null ? `KES ${subtotal.toLocaleString("en-KE", { minimumFractionDigits: 2 })}` : "—"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <div className="space-y-1 text-sm border-t pt-3">
                <div className="flex justify-between text-muted-foreground">
                  <span>Items Subtotal</span>
                  <span>KES {summary.itemsSubtotal.toLocaleString("en-KE", { minimumFractionDigits: 2 })}</span>
                </div>
                {summary.discountAmount > 0 && (
                  <div className="flex justify-between text-emerald-700">
                    <span>
                      Discount{summary.discountLabel ? ` (${summary.discountLabel})` : ""}
                      {summary.discountPct > 0 ? ` ${summary.discountPct.toFixed(2)}%` : ""}
                    </span>
                    <span>− KES {summary.discountAmount.toLocaleString("en-KE", { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                {requestSpecialDiscount && <p className="text-sm text-amber-700">Special discount requested: {specialDiscountKind === "Percentage" ? `${specialDiscountValue}%` : `KES ${specialDiscountValue}`}. Current total remains effective until GM approval.</p>}
                {summary.transportCost > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>Transport</span>
                    <span>KES {summary.transportCost.toLocaleString("en-KE", { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                {summary.trayDeposit > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>Refundable Tray Deposit</span>
                    <span>KES {summary.trayDeposit.toLocaleString("en-KE", { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-base border-t pt-2 mt-1">
                  <span>Grand Total</span>
                  <span>KES {summary.grandTotal.toLocaleString("en-KE", { minimumFractionDigits: 2 })}</span>
                </div>
                {showPointsEstimate && (
                  <div className="flex justify-between items-baseline text-sm text-muted-foreground pt-1">
                    <span>Loyalty points earned (est.)</span>
                    <span className="font-medium text-foreground">
                      {pointsEstimate!.points.toLocaleString("en-KE")} pts
                    </span>
                  </div>
                )}
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setShowConfirm(false)} disabled={createOrderMutation.isPending}>
                  Back
                </Button>
                <Button
                  onClick={() => { setShowConfirm(false); handleSubmit(); }}
                  disabled={createOrderMutation.isPending}
                >
                  {createOrderMutation.isPending ? "Creating..." : "Confirm & Create"}
                </Button>
              </div>
            </div>
          );
        })()}
      </DialogContent>
    </Dialog>
    </>
  );
};
